import { useState, useEffect, useRef } from "react";

// ─── BLS-ANCHORED BENCHMARK DATA ─────────────────────────────────────────────
// Source: BLS Occupational Employment & Wage Statistics + McKinsey career research
// Salary bands by industry + seniority level (base salary, USD, national median)
const BLS_BENCHMARKS = {
  "Chemicals / Manufacturing": {
    bands: [
      { level: 1, title: "Technician / Associate",    years: [0,3],  p25: 46000,  p50: 58000,  p75: 72000  },
      { level: 2, title: "Engineer / Specialist",     years: [2,6],  p25: 65000,  p50: 82000,  p75: 102000 },
      { level: 3, title: "Senior Engineer / Lead",    years: [5,10], p25: 88000,  p50: 110000, p75: 138000 },
      { level: 4, title: "Supervisor / Manager",      years: [8,14], p25: 108000, p50: 136000, p75: 172000 },
      { level: 5, title: "Plant / Operations Director",years:[12,18],p25: 140000, p50: 180000, p75: 240000 },
      { level: 6, title: "VP / General Manager",      years: [17,99],p25: 185000, p50: 270000, p75: 480000 },
    ],
    promotionYears: 3.6,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 325 Chemicals + 31-33 Manufacturing; UK SIC 20000/24000; CA NAICS same; AU ANZSIC C
  },

  "Construction / Trades": {
    bands: [
      { level: 1, title: "Apprentice / Laborer",   years: [0,3],  p25: 38000,  p50: 48000,  p75: 60000  },
      { level: 2, title: "Journeyman / Tradesperson",years: [2,6], p25: 55000,  p50: 68000,  p75: 85000  },
      { level: 3, title: "Senior Tradesperson",      years: [5,10], p25: 70000,  p50: 86000,  p75: 108000 },
      { level: 4, title: "Supervisor / Foreman",     years: [8,14], p25: 82000,  p50: 102000, p75: 128000 },
      { level: 5, title: "Project Manager",          years: [12,18],p25: 98000,  p50: 125000, p75: 162000 },
      { level: 6, title: "Director / Owner",         years: [17,99],p25: 130000, p50: 185000, p75: 320000 },
    ],
    promotionYears: 4.2,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 23; UK SIC 41-43; CA NAICS 23; AU ANZSIC E
  },

  "Creative / Media": {
    bands: [
      { level: 1, title: "Junior / Assistant",    years: [0,3],   p25: 36000,  p50: 46000,  p75: 58000  },
      { level: 2, title: "Mid-level",             years: [2,6],   p25: 50000,  p50: 64000,  p75: 80000  },
      { level: 3, title: "Senior",                years: [5,10],  p25: 65000,  p50: 82000,  p75: 105000 },
      { level: 4, title: "Lead / Manager",        years: [8,14],  p25: 82000,  p50: 105000, p75: 138000 },
      { level: 5, title: "Director / Head of",    years: [12,18], p25: 105000, p50: 140000, p75: 190000 },
      { level: 6, title: "VP / Executive Creative",years:[16,99], p25: 145000, p50: 210000, p75: 380000 },
    ],
    promotionYears: 3.8,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },

  "Education / Nonprofit": {
    bands: [
      { level: 1, title: "Entry / Program Staff", years: [0,3],   p25: 35000,  p50: 44000,  p75: 55000  },
      { level: 2, title: "Coordinator / Educator", years: [2,7],  p25: 44000,  p50: 56000,  p75: 70000  },
      { level: 3, title: "Senior / Specialist",   years: [6,12],  p25: 55000,  p50: 70000,  p75: 88000  },
      { level: 4, title: "Manager / Lead",        years: [10,16], p25: 68000,  p50: 85000,  p75: 108000 },
      { level: 5, title: "Director",              years: [14,20], p25: 85000,  p50: 110000, p75: 145000 },
      { level: 6, title: "VP / Executive Director",years:[18,99], p25: 115000, p50: 160000, p75: 250000 },
    ],
    promotionYears: 4.8,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },

  "Energy / Oil & Gas / Utilities": {
    bands: [
      { level: 1, title: "Graduate / Technician",    years: [0,3],  p25: 58000,  p50: 72000,  p75: 90000  },
      { level: 2, title: "Engineer / Analyst",        years: [2,6],  p25: 82000,  p50: 104000, p75: 130000 },
      { level: 3, title: "Senior Engineer / Specialist",years:[5,10],p25: 110000, p50: 140000, p75: 178000 },
      { level: 4, title: "Lead / Superintendent",     years: [8,14], p25: 140000, p50: 180000, p75: 232000 },
      { level: 5, title: "Manager / Superintendent",  years: [12,18],p25: 175000, p50: 230000, p75: 310000 },
      { level: 6, title: "VP / General Manager",      years: [17,99],p25: 240000, p50: 360000, p75: 650000 },
    ],
    promotionYears: 3.1,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 211 Oil/Gas + 2211 Electric Power + 2212 Natural Gas; UK SIC 06000/35000 (North Sea);
    // CA NAICS 211 (Alberta/Calgary); AU ANZSIC 0700/2600 (Perth/Darwin LNG)
    // Note: offshore/FIFO roles carry significant location premiums — geo multiplier handles this
  },

  "Engineering": {
    bands: [
      { level: 1, title: "Junior Engineer",        years: [0,2],   p25: 65000,  p50: 80000,  p75: 96000  },
      { level: 2, title: "Engineer II",            years: [2,5],   p25: 85000,  p50: 105000, p75: 128000 },
      { level: 3, title: "Senior Engineer",        years: [5,9],   p25: 108000, p50: 135000, p75: 168000 },
      { level: 4, title: "Lead / Principal",       years: [8,13],  p25: 135000, p50: 172000, p75: 218000 },
      { level: 5, title: "Engineering Manager",    years: [12,18], p25: 165000, p50: 215000, p75: 285000 },
      { level: 6, title: "VP Engineering / CTO",  years: [17,99], p25: 220000, p50: 310000, p75: 480000 },
    ],
    promotionYears: 2.9,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 54133 Engineering Services; ANZSIC 6910 AU; NOC 213x CA; SIC 8711 UK
  },

  "Finance": {
    bands: [
      { level: 1, title: "Analyst",               years: [0,3],   p25: 62000,  p50: 78000,  p75: 95000  },
      { level: 2, title: "Associate",             years: [2,6],   p25: 85000,  p50: 108000, p75: 135000 },
      { level: 3, title: "Senior Associate / VP", years: [5,10],  p25: 115000, p50: 152000, p75: 200000 },
      { level: 4, title: "Director / SVP",        years: [9,15],  p25: 165000, p50: 220000, p75: 300000 },
      { level: 5, title: "Managing Director",     years: [14,20], p25: 230000, p50: 320000, p75: 500000 },
      { level: 6, title: "Partner / C-Suite",     years: [18,99], p25: 350000, p50: 550000, p75: 1200000},
    ],
    promotionYears: 3.2,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },

  "Government / Public Sector": {
    bands: [
      { level: 1, title: "Grade 1–3 / APS1–3",      years: [0,3],  p25: 42000,  p50: 52000,  p75: 64000  },
      { level: 2, title: "Grade 4–5 / APS4–5",      years: [2,7],  p25: 54000,  p50: 66000,  p75: 82000  },
      { level: 3, title: "Grade 6–7 / APS6–EL1",    years: [6,12], p25: 68000,  p50: 84000,  p75: 104000 },
      { level: 4, title: "Manager / EL2 / HEO",     years: [10,16],p25: 85000,  p50: 105000, p75: 132000 },
      { level: 5, title: "Senior Manager / SES1",   years: [14,20],p25: 108000, p50: 138000, p75: 178000 },
      { level: 6, title: "Executive / SES2–3",      years: [18,99],p25: 148000, p50: 210000, p75: 340000 },
    ],
    promotionYears: 4.5,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 92; UK SIC 84; CA NOC 0411; AU APS/SES scale
  },

  "Healthcare": {
    bands: [
      { level: 1, title: "Entry / Coordinator",   years: [0,3],   p25: 42000,  p50: 54000,  p75: 68000  },
      { level: 2, title: "Specialist / Clinician", years: [2,7],  p25: 58000,  p50: 75000,  p75: 95000  },
      { level: 3, title: "Senior Specialist",     years: [6,12],  p25: 78000,  p50: 98000,  p75: 125000 },
      { level: 4, title: "Manager / Supervisor",  years: [10,16], p25: 95000,  p50: 120000, p75: 155000 },
      { level: 5, title: "Director",              years: [14,20], p25: 120000, p50: 158000, p75: 210000 },
      { level: 6, title: "VP / C-Suite",          years: [18,99], p25: 175000, p50: 260000, p75: 420000 },
    ],
    promotionYears: 4.1,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },

  "Hospitality / Tourism / Food Service": {
    bands: [
      { level: 1, title: "Front-line / Junior",      years: [0,2],  p25: 28000,  p50: 36000,  p75: 46000  },
      { level: 2, title: "Supervisor / Team Lead",   years: [2,5],  p25: 38000,  p50: 48000,  p75: 60000  },
      { level: 3, title: "Manager",                  years: [4,9],  p25: 50000,  p50: 64000,  p75: 80000  },
      { level: 4, title: "Senior Manager / GM",      years: [7,13], p25: 65000,  p50: 84000,  p75: 108000 },
      { level: 5, title: "Director / Regional GM",   years: [11,17],p25: 88000,  p50: 118000, p75: 160000 },
      { level: 6, title: "VP / C-Suite",             years: [16,99],p25: 130000, p50: 200000, p75: 380000 },
    ],
    promotionYears: 3.0,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 72 Accommodation & Food Services; UK SIC 55000/56000; CA NAICS 72; AU ANZSIC H
  },

  "Human Resources": {
    bands: [
      { level: 1, title: "HR Coordinator",         years: [0,2],  p25: 40000,  p50: 50000,  p75: 62000  },
      { level: 2, title: "HR Generalist / Spec.",  years: [2,5],  p25: 54000,  p50: 68000,  p75: 86000  },
      { level: 3, title: "Senior HR Specialist",   years: [4,8],  p25: 70000,  p50: 88000,  p75: 112000 },
      { level: 4, title: "HR Manager",             years: [7,13], p25: 88000,  p50: 112000, p75: 145000 },
      { level: 5, title: "HR Director",            years: [11,17],p25: 115000, p50: 152000, p75: 205000 },
      { level: 6, title: "VP HR / CHRO",           years: [16,99],p25: 165000, p50: 240000, p75: 420000 },
    ],
    promotionYears: 3.8,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS SOC 13-1071; UK SIC 7830; CA NOC 112x; AU ANZSIC 7000
  },

  "Legal": {
    bands: [
      { level: 1, title: "Paralegal / Trainee",   years: [0,3],   p25: 45000,  p50: 58000,  p75: 74000  },
      { level: 2, title: "Associate Attorney",    years: [2,6],   p25: 82000,  p50: 115000, p75: 160000 },
      { level: 3, title: "Senior Associate",      years: [5,10],  p25: 130000, p50: 185000, p75: 260000 },
      { level: 4, title: "Of Counsel / Counsel",  years: [9,15],  p25: 180000, p50: 250000, p75: 360000 },
      { level: 5, title: "Partner (non-equity)",  years: [13,19], p25: 230000, p50: 340000, p75: 520000 },
      { level: 6, title: "Equity Partner",        years: [18,99], p25: 350000, p50: 600000, p75: 1500000},
    ],
    promotionYears: 3.5,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 5411; UK SIC 6910; CA NOC 4112; AU ANZSIC 6910
  },

  "Marketing": {
    bands: [
      { level: 1, title: "Coordinator / Assistant", years: [0,2], p25: 40000, p50: 50000,  p75: 63000  },
      { level: 2, title: "Specialist / Executive", years: [2,5],  p25: 52000, p50: 66000,  p75: 82000  },
      { level: 3, title: "Senior Specialist",      years: [4,8],  p25: 68000, p50: 85000,  p75: 108000 },
      { level: 4, title: "Manager / Lead",         years: [7,12], p25: 85000, p50: 108000, p75: 138000 },
      { level: 5, title: "Director",               years: [11,17],p25: 115000,p50: 150000, p75: 200000 },
      { level: 6, title: "VP / CMO",               years: [16,99],p25: 165000,p50: 240000, p75: 420000 },
    ],
    promotionYears: 3.5,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },

  "Operations / Consulting": {
    bands: [
      { level: 1, title: "Analyst / Associate",   years: [0,3],   p25: 55000,  p50: 70000,  p75: 88000  },
      { level: 2, title: "Consultant / Senior",   years: [2,6],   p25: 78000,  p50: 98000,  p75: 125000 },
      { level: 3, title: "Manager",               years: [5,10],  p25: 105000, p50: 135000, p75: 170000 },
      { level: 4, title: "Senior Manager",        years: [8,14],  p25: 135000, p50: 175000, p75: 225000 },
      { level: 5, title: "Principal / Director",  years: [12,18], p25: 175000, p50: 230000, p75: 310000 },
      { level: 6, title: "Partner / VP",          years: [17,99], p25: 240000, p50: 350000, p75: 600000 },
    ],
    promotionYears: 3.0,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },

  "Pharmaceuticals / Life Sciences": {
    bands: [
      { level: 1, title: "Research Associate / Analyst", years: [0,3],  p25: 58000,  p50: 72000,  p75: 90000  },
      { level: 2, title: "Scientist / Specialist",       years: [2,6],  p25: 80000,  p50: 100000, p75: 125000 },
      { level: 3, title: "Senior Scientist / Manager",   years: [5,10], p25: 105000, p50: 132000, p75: 165000 },
      { level: 4, title: "Principal / Sr. Manager",      years: [8,14], p25: 135000, p50: 170000, p75: 215000 },
      { level: 5, title: "Director",                     years: [12,18],p25: 170000, p50: 220000, p75: 290000 },
      { level: 6, title: "VP / C-Suite",                 years: [17,99],p25: 235000, p50: 340000, p75: 600000 },
    ],
    promotionYears: 3.2,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 3254 Pharma Mfg + 5417 R&D; UK SIC 21000; CA NAICS 3254; AU ANZSIC 1840
  },

  "Real Estate": {
    bands: [
      { level: 1, title: "Agent / Analyst",         years: [0,3],  p25: 38000,  p50: 52000,  p75: 72000  },
      { level: 2, title: "Senior Agent / Associate", years: [2,6],  p25: 58000,  p50: 80000,  p75: 115000 },
      { level: 3, title: "Broker / Manager",         years: [5,10], p25: 82000,  p50: 115000, p75: 165000 },
      { level: 4, title: "Senior Manager / Director",years: [9,15], p25: 115000, p50: 160000, p75: 230000 },
      { level: 5, title: "Principal / Partner",      years: [13,20],p25: 155000, p50: 230000, p75: 380000 },
      { level: 6, title: "C-Suite / Owner",          years: [18,99],p25: 210000, p50: 350000, p75: 800000 },
    ],
    promotionYears: 4.0,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 531; UK SIC 6820; CA NAICS 531; AU ANZSIC 6700
  },

  "Retail / Consumer Goods": {
    bands: [
      { level: 1, title: "Sales Associate / Buyer Asst.", years: [0,2], p25: 30000, p50: 38000, p75: 48000 },
      { level: 2, title: "Supervisor / Buyer",            years: [2,5], p25: 42000, p50: 54000, p75: 68000 },
      { level: 3, title: "Store / Category Manager",      years: [4,9], p25: 58000, p50: 74000, p75: 94000 },
      { level: 4, title: "Senior Manager / Brand Mgr",    years: [7,13],p25: 78000, p50: 100000,p75: 130000 },
      { level: 5, title: "Director",                      years: [11,17],p25:105000, p50:140000, p75:185000 },
      { level: 6, title: "VP / GM / C-Suite",             years: [16,99],p25:148000, p50:220000, p75:400000 },
    ],
    promotionYears: 3.3,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 44-45 Retail Trade; UK SIC 47000; CA NAICS 44-45; AU ANZSIC G
  },

  "Sales": {
    bands: [
      { level: 1, title: "Sales Development Rep",  years: [0,2],  p25: 42000,  p50: 54000,  p75: 68000  },
      { level: 2, title: "Account Executive",      years: [1,5],  p25: 62000,  p50: 82000,  p75: 110000 },
      { level: 3, title: "Senior AE / Regional",   years: [4,8],  p25: 88000,  p50: 118000, p75: 160000 },
      { level: 4, title: "Sales Manager",          years: [7,12], p25: 110000, p50: 148000, p75: 200000 },
      { level: 5, title: "Director of Sales",      years: [11,17],p25: 145000, p50: 200000, p75: 280000 },
      { level: 6, title: "VP Sales / CRO",         years: [16,99],p25: 200000, p50: 310000, p75: 600000 },
    ],
    promotionYears: 2.5,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS SOC 41-0000 Sales; note: OTE (base+commission) — these are base figures
  },

  "Supply Chain / Logistics": {
    bands: [
      { level: 1, title: "Coordinator / Analyst",  years: [0,2],  p25: 42000,  p50: 52000,  p75: 65000  },
      { level: 2, title: "Specialist / Planner",   years: [2,5],  p25: 58000,  p50: 72000,  p75: 90000  },
      { level: 3, title: "Senior Specialist",       years: [5,9],  p25: 75000,  p50: 94000,  p75: 118000 },
      { level: 4, title: "Manager",                 years: [8,13], p25: 95000,  p50: 120000, p75: 152000 },
      { level: 5, title: "Director",                years: [12,18],p25: 125000, p50: 162000, p75: 215000 },
      { level: 6, title: "VP / Chief Supply Officer",years:[17,99],p25: 170000, p50: 250000, p75: 420000 },
    ],
    promotionYears: 3.4,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS NAICS 48-49 Transportation & Warehousing + 42 Wholesale; CA NAICS same; AU ANZSIC 46/53
  },

  "Technology": {
    bands: [
      { level: 1, title: "Junior / Entry",       years: [0,2],   p25: 68000,  p50: 82000,  p75: 98000  },
      { level: 2, title: "Mid-level",             years: [2,5],   p25: 90000,  p50: 112000, p75: 135000 },
      { level: 3, title: "Senior",                years: [5,9],   p25: 118000, p50: 148000, p75: 185000 },
      { level: 4, title: "Staff / Lead",          years: [8,13],  p25: 148000, p50: 190000, p75: 240000 },
      { level: 5, title: "Principal / Director",  years: [12,18], p25: 185000, p50: 240000, p75: 320000 },
      { level: 6, title: "VP / Executive",        years: [17,99], p25: 240000, p50: 340000, p75: 500000 },
    ],
    promotionYears: 2.8,
    dataYear: 2023,
    dataSource: "BLS OEWS",
    // BLS avg years per level in tech
  },

  "Other": {
    bands: [
      { level: 1, title: "Entry Level",           years: [0,2],   p25: 38000,  p50: 48000,  p75: 60000  },
      { level: 2, title: "Mid-level",             years: [2,5],   p25: 52000,  p50: 66000,  p75: 82000  },
      { level: 3, title: "Senior",                years: [5,9],   p25: 68000,  p50: 86000,  p75: 108000 },
      { level: 4, title: "Lead / Manager",        years: [8,13],  p25: 86000,  p50: 110000, p75: 140000 },
      { level: 5, title: "Director",              years: [12,18], p25: 110000, p50: 148000, p75: 200000 },
      { level: 6, title: "VP / Executive",        years: [17,99], p25: 155000, p50: 230000, p75: 400000 },
    ],
    promotionYears: 3.5,
    dataYear: 2023,
    dataSource: "BLS OEWS",
  },
};
// ─── GEOGRAPHIC DATA SYSTEM ──────────────────────────────────────────────────
// Tiered by data quality. Multipliers are relative to US national median (1.00).
// All salary outputs converted to local currency for display.
//
// Tier 1 🟢 — BLS OEWS metro-level data (US). Highest precision.
// Tier 2 🟡 — National stats agency data: ONS (UK), StatsCan (CA), ABS (AU),
//             Destatis (DE), MOM Singapore. Good precision.
// Tier 3 🟠 — Eurostat / modeled from comparable economies. Broad estimate.
// Tier 4 🔴 — Modelled assumptions. Directional only.

const GEO_DATA = {
  // ── UNITED STATES ─────────────────────────────────────────────────────────
  "United States": {
    currency: "USD", symbol: "$", flag: "🇺🇸",
    cities: [
      // BLS OEWS Tier 1 metros — alphabetical
      { name: "Atlanta, GA",                    mult: 0.97, tier: 1, source: "BLS OEWS Atlanta metro", dataYear: 2023 },
      { name: "Austin, TX",                     mult: 1.12, tier: 1, source: "BLS OEWS Austin metro", dataYear: 2023 },
      { name: "Baltimore, MD",                  mult: 1.09, tier: 1, source: "BLS OEWS Baltimore metro", dataYear: 2023 },
      { name: "Boston, MA",                     mult: 1.32, tier: 1, source: "BLS OEWS Boston metro", dataYear: 2023 },
      { name: "Charlotte, NC",                  mult: 0.98, tier: 1, source: "BLS OEWS Charlotte metro", dataYear: 2023 },
      { name: "Chicago, IL",                    mult: 1.10, tier: 1, source: "BLS OEWS Chicago metro", dataYear: 2023 },
      { name: "Cincinnati, OH",                 mult: 0.93, tier: 1, source: "BLS OEWS Cincinnati metro", dataYear: 2023 },
      { name: "Cleveland, OH",                  mult: 0.91, tier: 1, source: "BLS OEWS Cleveland metro", dataYear: 2023 },
      { name: "Columbus, OH",                   mult: 0.94, tier: 1, source: "BLS OEWS Columbus metro", dataYear: 2023 },
      { name: "Dallas / Fort Worth, TX",        mult: 1.08, tier: 1, source: "BLS OEWS Dallas metro", dataYear: 2023 },
      { name: "Denver, CO",                     mult: 1.06, tier: 1, source: "BLS OEWS Denver metro", dataYear: 2023 },
      { name: "Detroit, MI",                    mult: 0.97, tier: 1, source: "BLS OEWS Detroit metro", dataYear: 2023 },
      { name: "Houston, TX",                    mult: 1.05, tier: 1, source: "BLS OEWS Houston metro", dataYear: 2023 },
      { name: "Indianapolis, IN",               mult: 0.92, tier: 1, source: "BLS OEWS Indianapolis metro", dataYear: 2023 },
      { name: "Kansas City, MO",                mult: 0.93, tier: 1, source: "BLS OEWS Kansas City metro", dataYear: 2023 },
      { name: "Las Vegas, NV",                  mult: 0.94, tier: 1, source: "BLS OEWS Las Vegas metro", dataYear: 2023 },
      { name: "Los Angeles, CA",                mult: 1.24, tier: 1, source: "BLS OEWS LA metro", dataYear: 2023 },
      { name: "Miami, FL",                      mult: 0.98, tier: 1, source: "BLS OEWS Miami metro", dataYear: 2023 },
      { name: "Minneapolis, MN",                mult: 1.06, tier: 1, source: "BLS OEWS Minneapolis metro", dataYear: 2023 },
      { name: "Nashville, TN",                  mult: 0.99, tier: 1, source: "BLS OEWS Nashville metro", dataYear: 2023 },
      { name: "New York City, NY",              mult: 1.50, tier: 1, source: "BLS OEWS NYC metro", dataYear: 2023 },
      { name: "Orlando, FL",                    mult: 0.91, tier: 1, source: "BLS OEWS Orlando metro", dataYear: 2023 },
      { name: "Philadelphia, PA",               mult: 1.14, tier: 1, source: "BLS OEWS Philadelphia metro", dataYear: 2023 },
      { name: "Phoenix, AZ",                    mult: 1.00, tier: 1, source: "BLS OEWS Phoenix metro", dataYear: 2023 },
      { name: "Pittsburgh, PA",                 mult: 0.93, tier: 1, source: "BLS OEWS Pittsburgh metro", dataYear: 2023 },
      { name: "Portland, OR",                   mult: 1.12, tier: 1, source: "BLS OEWS Portland metro", dataYear: 2023 },
      { name: "Raleigh / Durham, NC",           mult: 1.04, tier: 1, source: "BLS OEWS Raleigh metro", dataYear: 2023 },
      { name: "Sacramento, CA",                 mult: 1.10, tier: 1, source: "BLS OEWS Sacramento metro", dataYear: 2023 },
      { name: "Salt Lake City, UT",             mult: 1.00, tier: 1, source: "BLS OEWS Salt Lake metro", dataYear: 2023 },
      { name: "San Antonio, TX",                mult: 0.92, tier: 1, source: "BLS OEWS San Antonio metro", dataYear: 2023 },
      { name: "San Diego, CA",                  mult: 1.18, tier: 1, source: "BLS OEWS San Diego metro", dataYear: 2023 },
      { name: "San Francisco / Bay Area, CA",   mult: 1.60, tier: 1, source: "BLS OEWS SF metro", dataYear: 2023 },
      { name: "Seattle, WA",                    mult: 1.38, tier: 1, source: "BLS OEWS Seattle metro", dataYear: 2023 },
      { name: "St. Louis, MO",                  mult: 0.93, tier: 1, source: "BLS OEWS St. Louis metro", dataYear: 2023 },
      { name: "Tampa, FL",                      mult: 0.94, tier: 1, source: "BLS OEWS Tampa metro", dataYear: 2023 },
      { name: "Washington D.C. / Northern VA",  mult: 1.32, tier: 1, source: "BLS OEWS DC metro", dataYear: 2023 },
      { name: "Remote / US-Based",              mult: 1.05, tier: 1, source: "BLS national median (remote premium)", dataYear: 2023 },
      { name: "Other US City",                  mult: 0.94, tier: 1, source: "BLS national median", dataYear: 2023 },
    ],
  },

  // ── UNITED KINGDOM ────────────────────────────────────────────────────────
  "United Kingdom": {
    currency: "GBP", symbol: "£", flag: "🇬🇧",
    cities: [
      { name: "London",                         mult: 1.20, tier: 2, source: "ONS ASHE 2023 — London", dataYear: 2023 },
      { name: "Manchester",                     mult: 0.88, tier: 2, source: "ONS ASHE 2023 — North West", dataYear: 2023 },
      { name: "Birmingham",                     mult: 0.86, tier: 2, source: "ONS ASHE 2023 — West Midlands", dataYear: 2023 },
      { name: "Edinburgh / Glasgow",            mult: 0.90, tier: 2, source: "ONS ASHE 2023 — Scotland", dataYear: 2023 },
      { name: "Bristol",                        mult: 0.92, tier: 2, source: "ONS ASHE 2023 — South West", dataYear: 2023 },
      { name: "Leeds",                          mult: 0.87, tier: 2, source: "ONS ASHE 2023 — Yorkshire", dataYear: 2023 },
      { name: "Other UK",                       mult: 0.84, tier: 2, source: "ONS ASHE 2023 — UK median", dataYear: 2023 },
    ],
  },

  // ── CANADA ────────────────────────────────────────────────────────────────
  "Canada": {
    currency: "CAD", symbol: "C$", flag: "🇨🇦",
    cities: [
      { name: "Toronto",                        mult: 0.90, tier: 2, source: "Statistics Canada LFS 2023 — Toronto CMA", dataYear: 2023 },
      { name: "Vancouver",                      mult: 0.88, tier: 2, source: "Statistics Canada LFS 2023 — Vancouver CMA", dataYear: 2023 },
      { name: "Calgary",                        mult: 0.89, tier: 2, source: "Statistics Canada LFS 2023 — Calgary CMA", dataYear: 2023 },
      { name: "Ottawa",                         mult: 0.87, tier: 2, source: "Statistics Canada LFS 2023 — Ottawa CMA", dataYear: 2023 },
      { name: "Montreal",                       mult: 0.82, tier: 2, source: "Statistics Canada LFS 2023 — Montreal CMA", dataYear: 2023 },
      { name: "Other Canada",                   mult: 0.80, tier: 2, source: "Statistics Canada LFS 2023 — national median", dataYear: 2023 },
    ],
  },

  // ── AUSTRALIA ─────────────────────────────────────────────────────────────
  "Australia": {
    currency: "AUD", symbol: "A$", flag: "🇦🇺",
    cities: [
      { name: "Sydney",                         mult: 0.96, tier: 2, source: "ABS Employee Earnings 2023 — Sydney", dataYear: 2023 },
      { name: "Melbourne",                      mult: 0.93, tier: 2, source: "ABS Employee Earnings 2023 — Melbourne", dataYear: 2023 },
      { name: "Brisbane",                       mult: 0.88, tier: 2, source: "ABS Employee Earnings 2023 — Brisbane", dataYear: 2023 },
      { name: "Perth",                          mult: 0.90, tier: 2, source: "ABS Employee Earnings 2023 — Perth", dataYear: 2023 },
      { name: "Canberra",                       mult: 0.95, tier: 2, source: "ABS Employee Earnings 2023 — Canberra", dataYear: 2023 },
      { name: "Adelaide",                       mult: 0.85, tier: 2, source: "ABS Employee Earnings 2023 — Adelaide", dataYear: 2023 },
      { name: "Other Australia",                mult: 0.84, tier: 2, source: "ABS Employee Earnings 2023 — national median", dataYear: 2023 },
    ],
  },

  // ── GERMANY ───────────────────────────────────────────────────────────────
  "Germany": {
    currency: "EUR", symbol: "€", flag: "🇩🇪",
    cities: [
      { name: "Berlin",                         mult: 0.80, tier: 2, source: "Destatis Earnings Survey 2022 — Berlin", dataYear: 2022 },
      { name: "Munich",                         mult: 0.92, tier: 2, source: "Destatis Earnings Survey 2022 — Bayern", dataYear: 2022 },
      { name: "Hamburg",                        mult: 0.86, tier: 2, source: "Destatis Earnings Survey 2022 — Hamburg", dataYear: 2022 },
      { name: "Frankfurt",                      mult: 0.88, tier: 2, source: "Destatis Earnings Survey 2022 — Hessen", dataYear: 2022 },
      { name: "Other Germany",                  mult: 0.78, tier: 2, source: "Destatis Earnings Survey 2022 — national median", dataYear: 2022 },
    ],
  },

  // ── SINGAPORE ─────────────────────────────────────────────────────────────
  "Singapore": {
    currency: "SGD", symbol: "S$", flag: "🇸🇬",
    cities: [
      { name: "Singapore",                      mult: 1.14, tier: 2, source: "MOM Occupational Wage Survey 2023", dataYear: 2023 },
    ],
  },

  // ── WESTERN EUROPE (Tier 3 — Eurostat modeled) ───────────────────────────
  "Western Europe": {
    currency: "EUR", symbol: "€", flag: "🇪🇺",
    cities: [
      { name: "Paris, France",                  mult: 0.84, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Amsterdam, Netherlands",         mult: 0.88, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Dublin, Ireland",                mult: 0.92, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Zurich / Geneva, Switzerland",   mult: 1.10, tier: 3, source: "Swiss FSO data + modeled", dataYear: 2023 },
      { name: "Stockholm, Sweden",              mult: 0.86, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Oslo, Norway",                   mult: 0.94, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Copenhagen, Denmark",            mult: 0.90, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Madrid / Barcelona, Spain",      mult: 0.66, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Milan / Rome, Italy",            mult: 0.70, tier: 3, source: "Eurostat earnings data + modeled", dataYear: 2023 },
      { name: "Other Western Europe",           mult: 0.74, tier: 3, source: "Eurostat median + modeled", dataYear: 2023 },
    ],
  },

  // ── ASIA-PACIFIC (Tier 3–4) ───────────────────────────────────────────────
  "Asia-Pacific": {
    currency: "USD", symbol: "$", flag: "🌏",
    cities: [
      { name: "Hong Kong",                      mult: 0.96, tier: 3, source: "HK Census & Statistics Dept + modeled", dataYear: 2023 },
      { name: "Tokyo, Japan",                   mult: 0.82, tier: 2, source: "Japan MHLW Basic Survey on Wage Structure 2023", dataYear: 2023 },
      { name: "Seoul, South Korea",             mult: 0.72, tier: 2, source: "Statistics Korea Wage Survey 2023", dataYear: 2023 },
      { name: "Shanghai / Beijing, China",      mult: 0.52, tier: 4, source: "Modelled — NBS data limited", dataYear: 2023 },
      { name: "Mumbai / Delhi, India",          mult: 0.24, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Bangalore, India",               mult: 0.28, tier: 4, source: "Modelled — tech premium applied", dataYear: 2023 },
      { name: "Kuala Lumpur, Malaysia",         mult: 0.34, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Jakarta, Indonesia",             mult: 0.22, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Manila, Philippines",            mult: 0.20, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Bangkok, Thailand",              mult: 0.26, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Auckland / Wellington, NZ",      mult: 0.82, tier: 2, source: "Statistics New Zealand Earnings Survey 2023", dataYear: 2023 },
      { name: "Other Asia-Pacific",             mult: 0.35, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
    ],
  },

  // ── MIDDLE EAST (Tier 3–4) ────────────────────────────────────────────────
  "Middle East": {
    currency: "USD", symbol: "$", flag: "🌍",
    cities: [
      { name: "Dubai / Abu Dhabi, UAE",         mult: 0.90, tier: 3, source: "UAE Ministry of HR data + modeled", dataYear: 2023 },
      { name: "Riyadh, Saudi Arabia",           mult: 0.72, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Other Middle East",              mult: 0.60, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
    ],
  },

  // ── LATIN AMERICA (Tier 4) ────────────────────────────────────────────────
  "Latin America": {
    currency: "USD", symbol: "$", flag: "🌎",
    cities: [
      { name: "São Paulo / Rio, Brazil",        mult: 0.28, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Mexico City, Mexico",            mult: 0.26, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Buenos Aires, Argentina",        mult: 0.20, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Bogotá, Colombia",               mult: 0.22, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Santiago, Chile",                mult: 0.30, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Other Latin America",            mult: 0.22, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
    ],
  },

  // ── AFRICA (Tier 4) ───────────────────────────────────────────────────────
  "Africa": {
    currency: "USD", symbol: "$", flag: "🌍",
    cities: [
      { name: "Lagos, Nigeria",                 mult: 0.14, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Johannesburg / Cape Town, SA",   mult: 0.24, tier: 3, source: "Stats SA Quarterly Employment Statistics 2023", dataYear: 2023 },
      { name: "Nairobi, Kenya",                 mult: 0.16, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Cairo, Egypt",                   mult: 0.14, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
      { name: "Other Africa",                   mult: 0.14, tier: 4, source: "Modelled — directional only", dataYear: 2023 },
    ],
  },

  // ── FRANCE (Tier 2 — INSEE DADS) ─────────────────────────────────────────
  "France": {
    currency: "EUR", symbol: "€", flag: "🇫🇷",
    cities: [
      { name: "Paris",                          mult: 0.82, tier: 2, source: "INSEE DADS wage data 2022", dataYear: 2022 },
      { name: "Lyon / Marseille",               mult: 0.71, tier: 2, source: "INSEE DADS wage data 2022", dataYear: 2022 },
      { name: "Other France",                   mult: 0.67, tier: 2, source: "INSEE DADS wage data 2022", dataYear: 2022 },
    ],
  },

  // ── NETHERLANDS (Tier 2 — CBS StatLine) ──────────────────────────────────
  "Netherlands": {
    currency: "EUR", symbol: "€", flag: "🇳🇱",
    cities: [
      { name: "Amsterdam / Rotterdam",          mult: 0.84, tier: 2, source: "CBS StatLine wage data 2023", dataYear: 2023 },
      { name: "Other Netherlands",              mult: 0.78, tier: 2, source: "CBS StatLine wage data 2023", dataYear: 2023 },
    ],
  },

  // ── SWITZERLAND (Tier 2 — Swiss FSO) ─────────────────────────────────────
  "Switzerland": {
    currency: "CHF", symbol: "CHF ", flag: "🇨🇭",
    cities: [
      { name: "Zurich / Geneva",                mult: 1.18, tier: 2, source: "Swiss FSO Wage Structure Survey 2022", dataYear: 2022 },
      { name: "Basel / Bern",                   mult: 1.08, tier: 2, source: "Swiss FSO Wage Structure Survey 2022", dataYear: 2022 },
      { name: "Other Switzerland",              mult: 1.02, tier: 2, source: "Swiss FSO Wage Structure Survey 2022", dataYear: 2022 },
    ],
  },

  // ── SWEDEN (Tier 2 — Statistics Sweden) ──────────────────────────────────
  "Sweden": {
    currency: "SEK", symbol: "kr", flag: "🇸🇪",
    cities: [
      { name: "Stockholm",                      mult: 0.86, tier: 2, source: "Statistics Sweden (SCB) wage data 2023", dataYear: 2023 },
      { name: "Gothenburg / Malmö",             mult: 0.80, tier: 2, source: "Statistics Sweden (SCB) wage data 2023", dataYear: 2023 },
      { name: "Other Sweden",                   mult: 0.76, tier: 2, source: "Statistics Sweden (SCB) wage data 2023", dataYear: 2023 },
    ],
  },

  // ── NORWAY (Tier 2 — Statistics Norway) ──────────────────────────────────
  "Norway": {
    currency: "NOK", symbol: "kr", flag: "🇳🇴",
    cities: [
      { name: "Oslo",                           mult: 0.96, tier: 2, source: "Statistics Norway (SSB) wage data 2023", dataYear: 2023 },
      { name: "Bergen / Stavanger",             mult: 0.90, tier: 2, source: "Statistics Norway (SSB) wage data 2023", dataYear: 2023 },
      { name: "Other Norway",                   mult: 0.86, tier: 2, source: "Statistics Norway (SSB) wage data 2023", dataYear: 2023 },
    ],
  },

  // ── DENMARK (Tier 2 — Danmarks Statistik) ────────────────────────────────
  "Denmark": {
    currency: "DKK", symbol: "kr", flag: "🇩🇰",
    cities: [
      { name: "Copenhagen",                     mult: 0.92, tier: 2, source: "Danmarks Statistik wage data 2023", dataYear: 2023 },
      { name: "Other Denmark",                  mult: 0.84, tier: 2, source: "Danmarks Statistik wage data 2023", dataYear: 2023 },
    ],
  },

  // ── NEW ZEALAND (Tier 2 — Statistics NZ) ─────────────────────────────────
  "New Zealand": {
    currency: "NZD", symbol: "NZ$", flag: "🇳🇿",
    cities: [
      { name: "Auckland",                       mult: 0.83, tier: 2, source: "Statistics New Zealand Earnings Survey 2023", dataYear: 2023 },
      { name: "Wellington / Christchurch",      mult: 0.80, tier: 2, source: "Statistics New Zealand Earnings Survey 2023", dataYear: 2023 },
      { name: "Other New Zealand",              mult: 0.74, tier: 2, source: "Statistics New Zealand Earnings Survey 2023", dataYear: 2023 },
    ],
  },

  // ── JAPAN (Tier 2 — MHLW) ────────────────────────────────────────────────
  "Japan": {
    currency: "JPY", symbol: "¥", flag: "🇯🇵",
    cities: [
      { name: "Tokyo",                          mult: 0.84, tier: 2, source: "Japan MHLW Basic Survey on Wage Structure 2023", dataYear: 2023 },
      { name: "Osaka / Nagoya",                 mult: 0.76, tier: 2, source: "Japan MHLW Basic Survey on Wage Structure 2023", dataYear: 2023 },
      { name: "Other Japan",                    mult: 0.70, tier: 2, source: "Japan MHLW Basic Survey on Wage Structure 2023", dataYear: 2023 },
    ],
  },

  // ── SOUTH KOREA (Tier 2 — Statistics Korea) ──────────────────────────────
  "South Korea": {
    currency: "KRW", symbol: "₩", flag: "🇰🇷",
    cities: [
      { name: "Seoul",                          mult: 0.74, tier: 2, source: "Statistics Korea Wage Survey 2023", dataYear: 2023 },
      { name: "Busan / Incheon",                mult: 0.66, tier: 2, source: "Statistics Korea Wage Survey 2023", dataYear: 2023 },
      { name: "Other South Korea",              mult: 0.62, tier: 2, source: "Statistics Korea Wage Survey 2023", dataYear: 2023 },
    ],
  },
};

// Currency conversion rates (approximate, for display only)
const FX_RATES = {
  "USD": 1.00, "GBP": 0.79, "CAD": 1.36, "AUD": 1.52,
  "EUR": 0.92, "SGD": 1.34, "CHF": 0.89, "SEK": 10.35,
  "NOK": 10.58, "DKK": 6.89, "NZD": 1.63, "JPY": 149.50,
  "KRW": 1325,
};

const TIER_META = {
  1: { label: "🟢 High confidence", color: "#2d6a4f" },
  2: { label: "🟡 Good estimate",   color: "#b5802a" },
  3: { label: "🟠 Broad estimate",  color: "#c05621" },
  4: { label: "🔴 Directional only",color: "#c0392b" },
};

// ─── PRICING & STRIPE ────────────────────────────────────────────────────────
// Replace these URLs with your actual Stripe payment links
const STRIPE_LINKS = {
  equityUnlock: "https://buy.stripe.com/bJeeVd8xSbqQ33kfM68bS01",
  pro:          "https://buy.stripe.com/14A00jaG02UkbzQ0Rc8bS00",
  coach:        "https://buy.stripe.com/REPLACE_COACH_MONTHLY",
};

const PRICING_TIERS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "",
    features: ["Career level assessment", "3rd party salary benchmarks", "Real-world career comparisons", "Shareable result card"],
    cta: "Current plan",
    highlight: false,
  },
  {
    id: "equity",
    name: "Equity Unlock",
    price: "$9",
    period: "one-time",
    features: ["Everything in Free", "Full equity gap analysis", "Systemic barrier breakdown", "PDF report export"],
    cta: "Unlock for $9",
    highlight: true,
    stripeLink: "equityUnlock",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$19",
    period: "/month",
    features: ["Unlimited reports", "Personal salary gap (real vs. market)", "Education level calibration", "Company size context", "Target role roadmap", "Saved report history", "PDF export"],
    cta: "Start Pro — $19/mo",
    highlight: false,
    stripeLink: "pro",
  },
  {
    id: "coach",
    name: "Coach",
    price: "$49",
    period: "/month",
    features: ["Everything in Pro", "Run reports for clients", "White-label PDF (your branding)", "White-label interface", "Client dashboard", "Compare client reports"],
    cta: "Start Coach — $49/mo",
    highlight: false,
    stripeLink: "coach",
  },
];

// ─── PRO WIZARD EXTRA QUESTIONS ───────────────────────────────────────────────
const PRO_QUESTIONS = [
  {
    id: "currentSalary",
    question: "What's your current salary?",
    subtext: "Used to calculate your <strong>real personal pay gap</strong> vs. market rate — not a population average. Kept private, never stored.",
    type: "salary",
    placeholder: "e.g. 95000",
  },
  {
    id: "education",
    question: "What's your highest level of education?",
    subtext: "Helps calibrate expected progression — an MBA or PhD typically changes the trajectory benchmark.",
    type: "select",
    options: [
      "High school / GED",
      "Some college",
      "Bachelor's degree",
      "Master's degree",
      "MBA",
      "PhD / Doctorate",
      "Professional degree (JD, MD, etc.)",
      "Trade / vocational qualification",
    ],
  },
  {
    id: "companySize",
    question: "What type of organization do you work for?",
    subtext: "Startup vs. enterprise vs. public sector changes what levels mean and what pay looks like significantly.",
    type: "select",
    options: [
      "Early-stage startup (< 50 people)",
      "Growth-stage startup (50–500 people)",
      "Mid-size company (500–5,000 people)",
      "Large enterprise (5,000+ people)",
      "Public sector / government",
      "Nonprofit / NGO",
      "Self-employed / freelance",
    ],
  },
  {
    id: "targetRole",
    question: "What role are you aiming for next?",
    subtext: "We'll build a specific roadmap showing your gap to get there — skills, timeline, and what's standing in the way.",
    type: "text",
    placeholder: "e.g. VP of Product, Partner, Chief Marketing Officer",
  },
];

function getGeoEntry(country, city) {
  const countryData = GEO_DATA[country];
  if (!countryData) return { mult: 0.94, tier: 1, source: "BLS national median", currency: "USD", symbol: "$", flag: "🌐" };
  const cityData = countryData.cities.find(c => c.name === city) || countryData.cities[countryData.cities.length - 1];
  return { ...cityData, currency: countryData.currency, symbol: countryData.symbol, flag: countryData.flag };
}

function getDataSourceLabel(country) {
  const labels = {
    "United States":  { name: "BLS",                dataYear: 2023 },
    "United Kingdom": { name: "ONS",                dataYear: 2023 },
    "Canada":         { name: "Statistics Canada",  dataYear: 2023 },
    "Australia":      { name: "ABS",                dataYear: 2023 },
    "Germany":        { name: "Destatis",           dataYear: 2022 },
    "Singapore":      { name: "MOM",                dataYear: 2023 },
    "France":         { name: "INSEE",              dataYear: 2022 },
    "Netherlands":    { name: "CBS",                dataYear: 2023 },
    "Switzerland":    { name: "Swiss FSO",          dataYear: 2022 },
    "Sweden":         { name: "Statistics Sweden",  dataYear: 2023 },
    "Norway":         { name: "Statistics Norway",  dataYear: 2023 },
    "Denmark":        { name: "Danmarks Statistik", dataYear: 2023 },
    "New Zealand":    { name: "Stats NZ",           dataYear: 2023 },
    "Japan":          { name: "MHLW Japan",         dataYear: 2023 },
    "South Korea":    { name: "Statistics Korea",   dataYear: 2023 },
  };
  return labels[country] || { name: "national salary data", dataYear: 2023 };
}

function applyGeo(salaryUSD, geoEntry) {
  const adjusted = salaryUSD * geoEntry.mult;
  const local = adjusted * (FX_RATES[geoEntry.currency] || 1.0);
  return Math.round(local / 1000) * 1000;
}

// McKinsey/BLS equity gap research data
const EQUITY_DATA = {
  "woman":                          { salaryGap: -18, promotionGap: -1.2, source: "McKinsey Women in the Workplace 2023" },
  "person of color":                { salaryGap: -19, promotionGap: -1.4, source: "BLS Earnings Report 2023" },
  "first-generation professional":  { salaryGap: -12, promotionGap: -0.8, source: "NALP Foundation Research 2022" },
  "career gap":                     { salaryGap: -8,  promotionGap: -0.6, source: "LinkedIn Economic Graph 2023" },
  "LGBTQ+":                         { salaryGap: -11, promotionGap: -0.7, source: "Williams Institute UCLA 2023" },
  "person with disability":         { salaryGap: -14, promotionGap: -0.9, source: "BLS Disability Employment Stats 2023" },
  "immigrant":                      { salaryGap: -10, promotionGap: -0.7, source: "National Academies Research 2023" },
  "non-hub location":               { salaryGap: -15, promotionGap: -0.5, source: "Brookings Geographic Opportunity Index 2022" },
};

function getBLSBenchmark(industry, yearsExp) {
  const data = BLS_BENCHMARKS[industry] || BLS_BENCHMARKS["Other"];
  const band = data.bands.find(b => yearsExp >= b.years[0] && yearsExp < b.years[1])
    || data.bands[data.bands.length - 1];
  return { band, promotionYears: data.promotionYears, allBands: data.bands };
}

function formatSalary(n, symbol="$") {
  if (!n) return "N/A";
  if (n >= 1000000) return `${symbol}${(n/1000000).toFixed(1)}M`;
  return `${symbol}${Math.round(n/1000)}K`;
}

// ─── WIZARD QUESTIONS ─────────────────────────────────────────────────────────
const QUESTIONS = [
  {
    id: "name",
    question: "First, what's your name?",
    subtext: "We'll personalize your report.",
    type: "text",
    placeholder: "Your first name",
  },
  {
    id: "startYear",
    question: "What year did you start your career?",
    subtext: "Include internships or part-time work if it was in your field.",
    type: "year",
    placeholder: "e.g. 2004",
  },
  {
    id: "industry",
    question: "What industry are you in?",
    subtext: "Pick the closest match — this anchors your salary benchmarks to real salary data.",
    type: "select",
    options: Object.keys(BLS_BENCHMARKS),
  },
  {
    id: "currentTitle",
    question: "What's your current job title?",
    subtext: "Be specific — this is how we calibrate your level.",
    type: "text",
    placeholder: "e.g. Senior Product Manager",
  },
  {
    id: "location",
    question: "Where are you based?",
    subtext: "Pick your country then city — we'll match it to the best available salary data.",
    type: "locationPicker",
  },
  {
    id: "context",
    question: "Tell us about your career path.",
    subtext: "Upload your resume <strong>or</strong> type a quick summary — key roles, any pivots, what you're proud of.",
    type: "resumeOrText",
    placeholder: "e.g. Started as a software engineer at a startup, moved into product management after 3 years, now leading a team of 8 at a Series B company...",
  },
  {
    id: "identity",
    question: "Do any of these apply to you?",
    subtext: "Optional. Used to surface equity gaps — where systemic factors may be affecting your career. Based on published research from McKinsey, national statistics agencies, and academic studies.",
    type: "multicheck",
    options: [
      { value: "woman",                         label: "Woman" },
      { value: "person of color",               label: "Person of Color" },
      { value: "first-generation professional", label: "First-gen professional" },
      { value: "career gap",                    label: "Career gap (parental / caregiving)" },
      { value: "LGBTQ+",                        label: "LGBTQ+" },
      { value: "person with disability",        label: "Person with disability" },
      { value: "immigrant",                     label: "Immigrant / Foreign-born" },
      { value: "non-hub location",              label: "Non-hub city or region" },
    ],
    optional: true,
  },
  {
    id: "ambition",
    question: "What does progress look like to you right now?",
    subtext: "This shapes your report. There's no right answer — we just want to reflect your actual goals, not assume them.",
    type: "select",
    options: [
      "I want to advance — take on more, earn more, move up",
      "I love what I do — I want to be paid fairly and keep getting better at it",
      "I'm weighing a change of direction",
      "I'm not sure — just show me where I stand",
    ],
  },
];

// ─── CLAUDE ANALYSIS ──────────────────────────────────────────────────────────
async function analyzeCareer(answers, blsData, attempt = 1) {
  const currentYear = new Date().getFullYear();
  const yearsExp = currentYear - parseInt(answers.startYear);
  const { band, promotionYears } = blsData;

  const expectedLevel = Math.min(Math.floor(yearsExp / promotionYears) + 1, 6);
  const geoEntry = blsData.geoEntry || getGeoEntry("United States", "Other US City");
  const salaryP50 = applyGeo(band.p50, geoEntry);
  const salaryP25 = applyGeo(band.p25, geoEntry);
  const salaryP75 = applyGeo(band.p75, geoEntry);

  const equityFactors = (answers.identity || []).map(id => EQUITY_DATA[id]).filter(Boolean);
  const totalSalaryGap = equityFactors.reduce((sum, f) => sum + f.salaryGap, 0);
  const totalPromotionGap = equityFactors.reduce((sum, f) => sum + f.promotionGap, 0);

  const prompt = `You are a career intelligence analyst. A user has completed a career assessment wizard. Use the pre-calculated benchmark data provided — do NOT recalculate salaries or years.

USER DATA:
- Name: ${answers.name}
- Career started: ${answers.startYear} (${yearsExp} years ago — today is ${currentYear})
- Industry: ${answers.industry}
- Current title: ${answers.currentTitle}
- Location: ${(()=>{try{const l=JSON.parse(answers.location||'{}');return (l.city||'')+(l.country?', '+l.country:'')}catch(e){return answers.location||''}})()}
- Career context: ${String(answers.context || "").slice(0, 3000)}
- Identity factors: ${(answers.identity||[]).join(", ") || "none provided"}
- Career ambition: ${answers.ambition || "not specified"}
${answers.currentSalary ? `- Current salary: $${answers.currentSalary} (USE THIS for personal gap analysis)` : ""}
${answers.education ? `- Education: ${answers.education}` : ""}
${answers.companySize ? `- Company type: ${answers.companySize}` : ""}
${answers.targetRole ? `- Target role: ${answers.targetRole} (build a specific roadmap to this)` : ""}

PRE-CALCULATED BLS BENCHMARKS (use these exact numbers):
- Expected title band for ${yearsExp} years in ${answers.industry}: "${band.title}"
- Expected level: ${expectedLevel} of 6
- Salary p25 (adjusted for ${answers.location}): $${salaryP25.toLocaleString()}
- Salary p50 / market rate: $${salaryP50.toLocaleString()}
- Salary p75: $${salaryP75.toLocaleString()}
- Avg years per promotion in this industry: ${promotionYears}
- Total salary gap from equity factors: ${totalSalaryGap}% (0 if no identity factors)
- Total promotion level gap from equity factors: ${totalPromotionGap} levels (0 if none)

${(() => {
  const a = answers.ambition || "";
  if (a.startsWith("I love what I do")) {
    return `AMBITION PATH: VOCATIONAL / MASTERY
This person has explicitly chosen depth over advancement. Adjust your entire response accordingly:
- NEVER use "behind", "on track", or "ahead" framing relative to a promotion ladder
- Do NOT frame the report as progression toward a higher level
- The standout sentence should affirm the value of their expertise depth, not hint at a next step upward
- In summary: focus on whether they are paid fairly for their level of mastery and experience
- In nextSteps: recommend ways to increase pay or recognition WITHOUT changing roles (e.g. certifications that command pay premiums, specialist designations, salary negotiation based on tenure)
- In realComparisons: choose figures known for depth of expertise and mastery in their field, NOT executives or people who climbed to leadership
- Set progressionPace to "mastery" (not ahead/on track/behind)
- Set paceDetail to a sentence about their depth vs. peers, e.g. "You're in the top quartile of experience depth for ICU nursing nationally"`;
  }
  if (a.startsWith("I'm weighing a change")) {
    return `AMBITION PATH: CAREER PIVOT
This person is considering a direction change. Adjust your response:
- Emphasise transferable skills and how they translate to adjacent roles or industries
- In nextSteps: include at least one step about exploring adjacent sectors or roles
- In realComparisons: choose figures who made notable career pivots
- Keep progression framing neutral — neither push them to advance nor stay`;
  }
  return `AMBITION PATH: ADVANCEMENT
Frame this report around career progression toward the next level. Use the standard benchmarking framing.`;
})()}

Return ONLY valid JSON:
{
  "totalYears": ${yearsExp},
  "currentLevelNum": <1-6 integer, assess from their title vs the band>,
  "expectedLevelNum": ${expectedLevel},
  "currentLevelTitle": <their assessed title band e.g. "Senior" — match to BLS band names>,
  "progressPct": <0-100, progress within their current band toward next>,
  "progressionPace": <"ahead" | "on track" | "behind" | "mastery" — use "mastery" if ambition path is vocational>,
  "paceDetail": <1 sentence, e.g. "You're 2 years ahead of the typical progression pace in Technology">,
  "standout": <1 punchy sentence on what makes their profile distinctive>,
  "summary": <2 sentences on their career arc — specific, not generic>,
  "topSkills": <array of 4-5 inferred skills from their context and title>,
  "nextSteps": <array of 3 concrete actions to reach next level>,
  "realComparisons": [
    {
      "name": <globally famous person — CEO/founder/executive>,
      "initials": <2 letters>,
      "knownFor": <their famous role>,
      "atYourStage": <1-2 sentences: what were they doing at ~${yearsExp} years into their career? Be specific — title, company, year>,
      "tag": <"Similar pivot" | "Same industry" | "Parallel path" | "Non-linear rise">
    }
  ],
  "equityAnalysis": {
    "hasFactors": <boolean>,
    "salaryGapPct": ${totalSalaryGap},
    "promotionGapLevels": ${totalPromotionGap},
    "adjustedSalaryP50": <salaryP50 adjusted by gap: ${Math.round(salaryP50 * (1 + totalSalaryGap/100))}>,
    "factors": [
      {
        "name": <factor name>,
        "icon": <emoji>,
        "active": <boolean — does this apply to the user>,
        "stat": <one specific cited statistic e.g. "Women earn 82¢ per dollar vs male peers (BLS 2023)">,
        "impact": <1 sentence on career impact>
      }
    ],
    "trueTitle": <what title they'd likely hold without systemic barriers>,
    "callout": <2-3 sentences, advocacy-forward, frank — written like an equity researcher not HR>
  }
}

For realComparisons: choose 2 famous figures whose documented early/mid career genuinely parallels this person's trajectory. CRITICAL: both must have worked in ${answers.industry} or a directly adjacent field — do not pick figures from unrelated industries. Be very specific about what they were doing at the equivalent career stage — title, company, year.`;

  // Retry with exponential backoff — up to 3 attempts
  const MAX_ATTEMPTS = 3;
  const BACKOFF_MS = [0, 3000, 7000]; // immediate, 3s, 7s

  if (attempt > 1) {
    await new Promise(r => setTimeout(r, BACKOFF_MS[attempt - 1] || 7000));
  }

  let response;
  try {
    response = await fetch("/api/analyze", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-5",
        max_tokens: 2000,
        messages: [{ role: "user", content: prompt }],
      }),
    });
  } catch (networkErr) {
    if (attempt < MAX_ATTEMPTS) return analyzeCareer(answers, blsData, attempt + 1);
    throw new Error("Network error — check your connection and try again.");
  }

  // Handle rate limits and server errors with retry
  if (response.status === 429 || response.status >= 500) {
    if (attempt < MAX_ATTEMPTS) return analyzeCareer(answers, blsData, attempt + 1);
    throw new Error(`Service temporarily busy (${response.status}). Please wait a moment and try again.`);
  }

  if (!response.ok) {
    // Read the actual error body from the API — this tells us what's really wrong
    let errBody = {};
    try { errBody = await response.json(); } catch(e) {}
    const errMsg = errBody?.error?.message || errBody?.message || JSON.stringify(errBody);
    console.error("API error body:", errBody);
    // 400 = bad request (prompt issue) — no point retrying same prompt
    if (response.status === 400) {
      throw new Error(`Bad request (400): ${errMsg}`);
    }
    throw new Error(`API error ${response.status}: ${errMsg}`);
  }

  const data = await response.json();

  // Handle API-level errors
  if (data.error) {
    if (attempt < MAX_ATTEMPTS) return analyzeCareer(answers, blsData, attempt + 1);
    throw new Error(data.error.message || "Analysis failed. Please try again.");
  }

  const text = data.content?.map(i => i.text || "").join("") || "";
  const clean = text.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();

  // If JSON parse fails, retry
  try {
    return JSON.parse(clean);
  } catch (parseErr) {
    if (attempt < MAX_ATTEMPTS) return analyzeCareer(answers, blsData, attempt + 1);
    throw new Error("Could not parse analysis. Please try again.");
  }
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --ink: #0a0a0f;
  --ink2: #1a1a2e;
  --paper: #fafaf7;
  --paper2: #f2f2ec;
  --rule: #e0dfd8;
  --accent: #e63946;
  --accent2: #2d6a4f;
  --gold: #c9a84c;
  --muted: #888880;
  --font-head: 'Playfair Display', Georgia, serif;
  --font-body: 'DM Sans', system-ui, sans-serif;
  --r: 3px;
}

body { background: var(--paper); color: var(--ink); font-family: var(--font-body); }

.app { min-height: 100vh; background: var(--paper); }

/* MASTHEAD */
.masthead {
  border-bottom: 2px solid var(--ink);
  padding: 20px 24px 18px;
  display: flex;
  align-items: baseline;
  gap: 16px;
  background: var(--paper);
  position: sticky;
  top: 0;
  z-index: 100;
}
.masthead-title {
  font-family: var(--font-head);
  font-size: clamp(22px, 4vw, 32px);
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--ink);
  line-height: 1;
}
.masthead-rule { flex: 1; height: 1px; background: var(--rule); }
.masthead-sub {
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 500;
}

/* PROGRESS BAR */
.progress-wrap {
  height: 3px;
  background: var(--rule);
  position: sticky;
  top: 62px;
  z-index: 99;
}
.progress-fill {
  height: 100%;
  background: var(--accent);
  transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

/* WIZARD */
.wizard {
  max-width: 600px;
  margin: 0 auto;
  padding: 64px 24px 100px;
  animation: fadeUp 0.4s ease;
}

.wizard-step {
  margin-bottom: 8px;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 500;
}

.wizard-q {
  font-family: var(--font-head);
  font-size: clamp(28px, 5vw, 42px);
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
  margin-bottom: 10px;
  letter-spacing: -0.02em;
}

.wizard-sub {
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 36px;
  line-height: 1.6;
  font-weight: 400;
}
.wizard-sub strong { color: var(--ink); font-weight: 600; }

.wizard-input {
  width: 100%;
  font-family: var(--font-body);
  font-size: 20px;
  font-weight: 400;
  color: var(--ink);
  background: transparent;
  border: none;
  border-bottom: 2px solid var(--ink);
  padding: 12px 0;
  outline: none;
  transition: border-color 0.2s;
  -webkit-appearance: none;
  border-radius: 0;
}
.wizard-input::placeholder { color: var(--rule); }
.wizard-input:focus { border-bottom-color: var(--accent); }

.wizard-textarea {
  width: 100%;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--ink);
  background: var(--paper2);
  border: 1px solid var(--rule);
  border-radius: var(--r);
  padding: 16px 18px;
  outline: none;
  resize: vertical;
  min-height: 120px;
  line-height: 1.7;
  transition: border-color 0.2s;
}
.wizard-textarea::placeholder { color: var(--muted); }
.wizard-textarea:focus { border-color: var(--ink); }

.wizard-select {
  width: 100%;
  font-family: var(--font-body);
  font-size: 18px;
  color: var(--ink);
  background: transparent;
  border: none;
  border-bottom: 2px solid var(--ink);
  padding: 12px 0;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  border-radius: 0;
}

/* Option grid for select */
.option-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 4px;
}
.option-btn {
  padding: 14px 16px;
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  background: var(--paper);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  font-weight: 400;
  line-height: 1.3;
}
.option-btn:hover { border-color: var(--ink); background: var(--paper2); }
.option-btn.selected { border-color: var(--ink); background: var(--ink); color: var(--paper); font-weight: 500; }

/* Multicheck */
.check-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}
.check-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 14px;
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  cursor: pointer;
  transition: all 0.15s;
  font-size: 13px;
  color: var(--ink);
  font-weight: 400;
  user-select: none;
}
.check-item:hover { border-color: var(--muted); }
.check-item.checked { border-color: var(--accent2); background: rgba(45,106,79,0.06); color: var(--accent2); font-weight: 500; }
.check-dot {
  width: 14px; height: 14px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 10px;
}

/* NAV BUTTONS */
.wizard-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
}
.btn-next {
  padding: 14px 32px;
  background: var(--ink);
  color: var(--paper);
  border: none;
  border-radius: var(--r);
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.01em;
}
.btn-next:hover { background: var(--accent); }
.btn-next:disabled { opacity: 0.3; cursor: not-allowed; }
.btn-next:disabled:hover { background: var(--ink); }
.btn-back {
  padding: 14px 20px;
  background: transparent;
  color: var(--muted);
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  font-family: var(--font-body);
  font-size: 15px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-back:hover { border-color: var(--muted); color: var(--ink); }
.skip-btn {
  margin-left: auto;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.skip-btn:hover { color: var(--ink); }

/* Pro mode badge */
.pro-badge {
  display: inline-flex; align-items: center; gap: 5px;
  background: linear-gradient(135deg, #c9a84c, #f0d060);
  color: #3a2a00; font-size: 10px; font-weight: 700;
  letter-spacing: 0.15em; text-transform: uppercase;
  padding: 3px 10px; border-radius: 2px;
}

/* Equity paywall — news fade style */
.equity-locked {
  position: relative;
  border-top: 3px solid var(--gold);
}
.equity-fade-wrap {
  position: relative;
  overflow: hidden;
  max-height: 380px; /* deeper peek — shows header + how it works + start of level compare */
}
.equity-fade-wrap::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 200px;
  background: linear-gradient(
    to bottom,
    rgba(250,250,247,0) 0%,
    rgba(250,250,247,0.5) 25%,
    rgba(250,250,247,0.88) 60%,
    rgba(250,250,247,1) 100%
  );
  pointer-events: none;
}
.equity-paywall {
  padding: 20px 24px 24px;
  text-align: center;
  border-top: 1px solid var(--rule);
  background: var(--paper);
}
.equity-headline-free {
  background: rgba(230,57,70,0.06);
  border: 1px solid rgba(230,57,70,0.2);
  border-radius: var(--r);
  padding: 14px 16px;
  margin-bottom: 20px;
  text-align: left;
}
.equity-headline-free-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 6px;
}
.equity-headline-free-stat {
  font-family: var(--font-head);
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.2;
}
.equity-headline-free-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 4px;
  line-height: 1.5;
}
.lock-icon { font-size: 32px; }
.lock-title {
  font-family: var(--font-head); font-size: 22px; font-weight: 700;
  color: var(--ink); line-height: 1.2;
}
.lock-sub { font-size: 14px; color: var(--muted); line-height: 1.6; max-width: 340px; }
.btn-unlock {
  padding: 13px 28px;
  background: var(--ink); color: var(--paper);
  border: none; border-radius: var(--r);
  font-family: var(--font-body); font-size: 15px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
  letter-spacing: 0.01em;
}
.btn-unlock:hover { background: var(--accent); }
.lock-also {
  font-size: 12px; color: var(--muted);
}
.lock-also a { color: var(--ink); cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }

/* Pricing modal */
.pricing-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(10,10,15,0.7);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}
.pricing-modal {
  background: var(--paper); border-radius: 4px;
  border: 1.5px solid var(--ink);
  max-width: 860px; width: 100%;
  max-height: 90vh; overflow-y: auto;
  animation: fadeUp 0.3s ease;
}
.pricing-header {
  padding: 32px 36px 24px;
  border-bottom: 1px solid var(--rule);
  display: flex; justify-content: space-between; align-items: flex-start;
}
.pricing-title {
  font-family: var(--font-head); font-size: 32px; font-weight: 900;
  color: var(--ink); letter-spacing: -0.02em;
}
.pricing-sub { font-size: 14px; color: var(--muted); margin-top: 4px; }
.btn-close {
  background: none; border: none; font-size: 24px;
  color: var(--muted); cursor: pointer; line-height: 1;
  padding: 4px; transition: color 0.15s;
}
.btn-close:hover { color: var(--ink); }
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-bottom: 1px solid var(--rule);
}
@media (max-width: 680px) { .pricing-grid { grid-template-columns: 1fr 1fr; } }
.pricing-card {
  padding: 28px 24px;
  border-right: 1px solid var(--rule);
  position: relative;
}
.pricing-card:last-child { border-right: none; }
.pricing-card.highlighted {
  background: var(--ink); color: var(--paper);
}
.pricing-card.highlighted .tier-name { color: rgba(255,255,255,0.6); }
.pricing-card.highlighted .tier-price { color: var(--paper); }
.pricing-card.highlighted .tier-feature { color: rgba(255,255,255,0.75); }
.pricing-card.highlighted .tier-feature::before { color: #ffd700; }
.tier-name {
  font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase;
  color: var(--muted); font-weight: 600; margin-bottom: 12px;
}
.tier-price {
  font-family: var(--font-head); font-size: 36px; font-weight: 900;
  color: var(--ink); line-height: 1; margin-bottom: 4px;
}
.tier-period { font-size: 13px; color: var(--muted); margin-bottom: 20px; }
.tier-features { list-style: none; margin-bottom: 24px; }
.tier-feature {
  font-size: 13px; color: var(--ink2); padding: 5px 0;
  display: flex; align-items: flex-start; gap: 8px; line-height: 1.4;
}
.tier-feature::before { content: "✓"; color: var(--accent2); font-weight: 700; flex-shrink: 0; }
.btn-tier {
  width: 100%; padding: 12px 16px;
  border: 1.5px solid var(--ink); border-radius: var(--r);
  background: transparent; color: var(--ink);
  font-family: var(--font-body); font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; letter-spacing: 0.01em;
}
.btn-tier:hover { background: var(--ink); color: var(--paper); }
.pricing-card.highlighted .btn-tier {
  background: var(--paper); color: var(--ink); border-color: var(--paper);
}
.pricing-card.highlighted .btn-tier:hover { background: rgba(255,255,255,0.85); }
.pricing-footer {
  padding: 16px 36px; font-size: 12px; color: var(--muted); text-align: center;
}

/* Pro wizard */
.pro-wizard-header {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 32px;
}
.pro-wizard-label {
  font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--muted); font-weight: 500;
}

/* Salary input */
.salary-input-wrap {
  position: relative;
}
.salary-prefix {
  position: absolute; left: 0; top: 50%; transform: translateY(-50%);
  font-size: 20px; color: var(--muted); pointer-events: none;
}
.salary-input-wrap .wizard-input { padding-left: 20px; }

/* PDF export button */
.btn-pdf {
  padding: 13px 28px;
  background: var(--accent2); color: white;
  border: none; border-radius: var(--r);
  font-family: var(--font-body); font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.btn-pdf:hover { background: #245a42; }

/* Upgrade strip on result card */
.upgrade-strip {
  background: linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%);
  padding: 24px 36px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}
.upgrade-strip-text { }
.upgrade-strip-label {
  font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase;
  color: rgba(255,255,255,0.4); margin-bottom: 4px; font-weight: 600;
}
.upgrade-strip-headline {
  font-family: var(--font-head); font-size: 18px; font-weight: 700; color: white;
}
.upgrade-strip-sub { font-size: 13px; color: rgba(255,255,255,0.5); margin-top: 2px; }
.upgrade-strip-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.btn-upgrade-primary {
  padding: 11px 22px; background: var(--accent); color: white;
  border: none; border-radius: var(--r);
  font-family: var(--font-body); font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background 0.15s; white-space: nowrap;
}
.btn-upgrade-primary:hover { background: #c0303c; }
.btn-upgrade-secondary {
  padding: 11px 22px; background: transparent; color: rgba(255,255,255,0.7);
  border: 1px solid rgba(255,255,255,0.2); border-radius: var(--r);
  font-family: var(--font-body); font-size: 13px;
  cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.btn-upgrade-secondary:hover { border-color: rgba(255,255,255,0.5); color: white; }

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

/* Location picker */
.loc-countries {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 20px;
}
.loc-country-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  background: var(--paper);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--ink);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  font-weight: 400;
}
.loc-country-btn:hover { border-color: var(--ink); background: var(--paper2); }
.loc-country-btn.selected { border-color: var(--ink); background: var(--ink); color: var(--paper); font-weight: 500; }
.loc-flag { font-size: 18px; }
.loc-cities {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 4px;
}
.loc-city-btn {
  padding: 10px 14px;
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  background: var(--paper);
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--ink);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
}
.loc-city-btn:hover { border-color: var(--ink); background: var(--paper2); }
.loc-city-btn.selected { border-color: var(--ink); background: var(--ink); color: var(--paper); font-weight: 500; }
.loc-back-link {
  background: none; border: none;
  font-family: var(--font-body); font-size: 13px;
  color: var(--muted); cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
  margin-bottom: 16px; padding: 0; display: inline-block;
}
.loc-back-link:hover { color: var(--ink); }
.loc-step-label {
  font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--muted); margin-bottom: 12px; font-weight: 500;
}

/* Resume or text dual mode */
.dual-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  overflow: hidden;
}
.dual-tab {
  flex: 1;
  padding: 11px 16px;
  background: transparent;
  border: none;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}
.dual-tab:first-child { border-right: 1.5px solid var(--rule); }
.dual-tab.active { background: var(--ink); color: var(--paper); }
.dual-tab:hover:not(.active) { background: var(--paper2); color: var(--ink); }

.upload-zone {
  border: 1.5px dashed var(--rule);
  border-radius: var(--r);
  padding: 36px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--paper2);
}
.upload-zone:hover, .upload-zone.has-file {
  border-color: var(--ink);
  background: var(--paper);
}
.upload-icon { font-size: 32px; margin-bottom: 10px; display: block; }
.upload-label { font-size: 15px; color: var(--ink); font-weight: 500; margin-bottom: 4px; }
.upload-sub { font-size: 12px; color: var(--muted); }
.upload-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 6px 14px;
  background: var(--ink);
  color: var(--paper);
  border-radius: 2px;
  font-size: 13px;
  font-weight: 500;
}

/* LOADING */
.loading-wrap {
  max-width: 480px;
  margin: 80px auto;
  padding: 0 24px;
  text-align: center;
  animation: fadeUp 0.4s ease;
}
.loading-spinner {
  width: 48px; height: 48px;
  border: 2px solid var(--rule);
  border-top-color: var(--ink);
  border-radius: 50%;
  margin: 0 auto 32px;
  animation: spin 0.9s linear infinite;
}
.loading-title {
  font-family: var(--font-head);
  font-size: 28px;
  font-weight: 700;
  color: var(--ink);
  margin-bottom: 8px;
}
.loading-steps { margin-top: 28px; text-align: left; display: inline-block; }
.loading-step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  font-size: 13px;
  color: var(--muted);
  transition: color 0.3s;
  letter-spacing: 0.02em;
}
.loading-step.active { color: var(--ink); font-weight: 500; }
.loading-step.done { color: var(--accent2); }
.step-icon { font-size: 14px; flex-shrink: 0; }

/* ── RESULT CARD ── */
.result-outer {
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 24px 100px;
  animation: fadeUp 0.5s ease;
}

.result-card {
  background: var(--paper);
  border: 1.5px solid var(--ink);
  border-radius: 4px;
  overflow: hidden;
}

/* Card header */
.card-header {
  background: var(--ink);
  color: var(--paper);
  padding: 32px 36px 28px;
  position: relative;
  overflow: hidden;
}
.card-header::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 3px;
  background: repeating-linear-gradient(90deg, var(--accent) 0px, var(--accent) 8px, transparent 8px, transparent 16px);
}
.card-header-eyebrow {
  font-size: 10px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  margin-bottom: 10px;
  font-weight: 500;
}
.card-name {
  font-family: var(--font-head);
  font-size: clamp(32px, 6vw, 52px);
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
}
.card-headline {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.card-level-badge {
  background: var(--accent);
  color: white;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 2px;
}
.card-title-text {
  font-size: 15px;
  color: rgba(255,255,255,0.7);
  font-weight: 400;
}

/* PACE STRIP */
.pace-strip {
  border-bottom: 1px solid var(--rule);
  padding: 18px 36px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--paper2);
}
.pace-icon { font-size: 22px; }
.pace-label {
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 2px;
  font-weight: 500;
}
.pace-text { font-size: 14px; color: var(--ink); font-weight: 500; }
.pace-text.ahead { color: var(--accent2); }
.pace-text.behind { color: var(--accent); }
.pace-detail { font-size: 13px; color: var(--muted); margin-left: auto; max-width: 300px; text-align: right; line-height: 1.4; }

/* XP BAR */
.xp-section {
  padding: 24px 36px;
  border-bottom: 1px solid var(--rule);
}
.xp-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 10px;
  font-weight: 500;
}
.xp-track {
  height: 6px;
  background: var(--rule);
  border-radius: 0;
  overflow: hidden;
}
.xp-fill {
  height: 100%;
  background: var(--ink);
  width: 0%;
  transition: width 1.4s cubic-bezier(0.16, 1, 0.3, 1);
}

/* TWO-COL LAYOUT */
.card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid var(--rule);
}
@media (max-width: 580px) { .card-body { grid-template-columns: 1fr; } }
.card-col { padding: 28px 36px; }
.card-col:first-child { border-right: 1px solid var(--rule); }
.col-label {
  font-size: 10px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.col-label::after { content: ''; flex: 1; height: 1px; background: var(--rule); }

/* SALARY */
.salary-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--rule);
}
.salary-row:last-child { border-bottom: none; }
.sal-label { font-size: 12px; color: var(--muted); }
.sal-value { font-family: var(--font-head); font-size: 22px; font-weight: 700; color: var(--ink); }
.sal-value.target { color: var(--accent); font-size: 26px; }
.sal-source { font-size: 10px; color: var(--muted); margin-top: 10px; letter-spacing: 0.05em; line-height: 1.5; }
.data-vintage { font-size: 11px; color: var(--muted); margin-top: 6px; letter-spacing: 0.01em; }

/* SKILLS */
.skill-list { display: flex; flex-wrap: wrap; gap: 7px; }
.skill-tag {
  font-size: 12px;
  padding: 5px 11px;
  border: 1px solid var(--rule);
  border-radius: 2px;
  color: var(--ink2);
  font-weight: 500;
  letter-spacing: 0.03em;
  animation: popIn 0.3s ease both;
}

/* COMPARISONS */
.comparisons-section {
  padding: 28px 36px;
  border-bottom: 1px solid var(--rule);
}
.comparison-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid var(--rule);
}
.comparison-item:last-child { border-bottom: none; padding-bottom: 0; }
.comp-avatar {
  width: 44px; height: 44px;
  background: var(--ink);
  color: var(--paper);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-head);
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}
.comp-name { font-weight: 600; font-size: 15px; color: var(--ink); margin-bottom: 2px; }
.comp-known { font-size: 11px; color: var(--muted); letter-spacing: 0.05em; margin-bottom: 6px; }
.comp-text { font-size: 13px; color: var(--ink2); line-height: 1.6; }
.comp-tag {
  display: inline-block;
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 1px solid var(--rule);
  color: var(--muted);
  margin-top: 7px;
  border-radius: 2px;
}

/* EQUITY SECTION */
.equity-section {
  border-top: 3px solid var(--accent);
  padding: 28px 36px;
}
.equity-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}
.equity-icon-wrap {
  width: 44px; height: 44px;
  background: var(--accent);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.equity-label {
  font-size: 10px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--accent);
  font-weight: 600;
  margin-bottom: 4px;
}
.equity-headline {
  font-family: var(--font-head);
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.2;
}

/* TRUE vs ACTUAL */
.level-compare {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: center;
  margin: 20px 0;
  border: 1px solid var(--rule);
  border-radius: var(--r);
  overflow: hidden;
}
.lc-box { padding: 18px 20px; }
.lc-box.actual { background: var(--paper2); }
.lc-box.true { background: rgba(45,106,79,0.06); border-left: 3px solid var(--accent2); }
.lc-arrow { padding: 0 16px; color: var(--muted); font-size: 18px; text-align: center; }
.lc-label { font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 4px; font-weight: 600; }
.lc-title { font-family: var(--font-head); font-size: 20px; font-weight: 700; color: var(--ink); }
.lc-box.true .lc-title { color: var(--accent2); }

/* GAP METERS */
.gap-meters { display: flex; flex-direction: column; gap: 14px; margin: 20px 0; }
.gm-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
.gm-label { font-size: 12px; color: var(--muted); font-weight: 500; }
.gm-value { font-family: var(--font-head); font-size: 22px; font-weight: 700; }
.gm-value.neg { color: var(--accent); }
.gm-value.pos { color: var(--accent2); }
.gm-track { height: 5px; background: var(--rule); border-radius: 0; overflow: hidden; }
.gm-fill { height: 100%; transition: width 1.4s cubic-bezier(0.16, 1, 0.3, 1); }
.gm-fill.neg { background: var(--accent); }
.gm-fill.pos { background: var(--accent2); }
.gm-sub { font-size: 11px; color: var(--muted); margin-top: 4px; }

/* Equity factors */
.equity-factors { display: flex; flex-direction: column; gap: 10px; margin: 20px 0; }
.ef-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--r);
}
.ef-row.active { background: rgba(230,57,70,0.05); border: 1px solid rgba(230,57,70,0.2); }
.ef-row.inactive { background: var(--paper2); border: 1px solid var(--rule); opacity: 0.65; }
.ef-icon { font-size: 18px; flex-shrink: 0; }
.ef-name { font-size: 12px; font-weight: 600; color: var(--ink); margin-bottom: 2px; letter-spacing: 0.03em; }
.ef-row.active .ef-name { color: var(--accent); }
.ef-stat { font-size: 12px; color: var(--muted); line-height: 1.5; }

/* Callout */
.equity-callout {
  background: var(--ink);
  color: var(--paper);
  padding: 20px 22px;
  border-radius: var(--r);
  margin-top: 20px;
}
.callout-label { font-size: 9px; letter-spacing: 0.25em; text-transform: uppercase; color: rgba(255,255,255,0.4); margin-bottom: 8px; font-weight: 600; }
.callout-text { font-size: 14px; line-height: 1.75; color: rgba(255,255,255,0.88); }

/* NEXT STEPS */
.next-section {
  padding: 28px 36px;
  border-top: 1px solid var(--rule);
}
.next-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--rule);
  font-size: 14px;
  color: var(--ink2);
  line-height: 1.55;
}
.next-item:last-child { border-bottom: none; }
.next-num {
  font-family: var(--font-head);
  font-size: 22px;
  font-weight: 700;
  color: var(--rule);
  flex-shrink: 0;
  line-height: 1;
  margin-top: -1px;
}

/* CARD FOOTER */
.card-footer {
  background: var(--paper2);
  border-top: 1px solid var(--rule);
  padding: 16px 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.footer-brand {
  font-family: var(--font-head);
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
}
.footer-source { font-size: 11px; color: var(--muted); line-height: 1.5; }

/* SHARE + RESTART */
.result-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;
  flex-wrap: wrap;
}
.btn-share {
  padding: 13px 28px;
  background: var(--ink);
  color: var(--paper);
  border: none;
  border-radius: var(--r);
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  letter-spacing: 0.02em;
}
.btn-share:hover { background: var(--accent); }

/* Share modal */
.share-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.share-modal {
  background: var(--paper); border-radius: 16px;
  padding: 32px 28px; max-width: 380px; width: 100%;
  box-shadow: 0 24px 80px rgba(0,0,0,0.18);
}
.share-modal-title {
  font-family: var(--font-head); font-size: 22px; font-weight: 700;
  margin-bottom: 6px; color: var(--ink);
}
.share-modal-sub {
  font-size: 13px; color: var(--muted); margin-bottom: 24px; line-height: 1.5;
}
.share-buttons {
  display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px;
}
.share-btn {
  display: flex; align-items: center; gap: 12px;
  padding: 13px 16px; border-radius: var(--r);
  border: 1.5px solid var(--rule); background: var(--paper);
  font-family: var(--font-body); font-size: 14px; font-weight: 500;
  cursor: pointer; color: var(--ink); text-decoration: none;
  transition: border-color 0.15s, background 0.15s;
}
.share-btn:hover { border-color: var(--ink); background: var(--paper2); }
.share-btn-icon { font-size: 18px; width: 24px; text-align: center; }
.share-copy-row {
  display: flex; gap: 8px; margin-bottom: 16px;
}
.share-copy-input {
  flex: 1; padding: 10px 12px; border: 1.5px solid var(--rule);
  border-radius: var(--r); font-family: var(--font-body); font-size: 12px;
  color: var(--muted); background: var(--paper2); overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap;
}
.share-copy-btn {
  padding: 10px 16px; background: var(--ink); color: var(--paper);
  border: none; border-radius: var(--r); font-family: var(--font-body);
  font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: background 0.15s;
}
.share-copy-btn:hover { background: var(--accent); }
.share-close {
  width: 100%; padding: 11px; background: transparent;
  border: 1.5px solid var(--rule); border-radius: var(--r);
  font-family: var(--font-body); font-size: 13px; color: var(--muted);
  cursor: pointer;
}
.share-close:hover { border-color: var(--ink); color: var(--ink); }
.btn-restart {
  padding: 13px 28px;
  background: transparent;
  color: var(--muted);
  border: 1.5px solid var(--rule);
  border-radius: var(--r);
  font-family: var(--font-body);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-restart:hover { border-color: var(--ink); color: var(--ink); }

/* DISCLAIMER */
.disclaimer {
  text-align: center;
  font-size: 11px;
  color: var(--muted);
  margin-top: 20px;
  line-height: 1.6;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

@keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
@keyframes spin { to { transform:rotate(360deg); } }
@keyframes popIn { from { opacity:0; transform:scale(0.85); } to { opacity:1; transform:scale(1); } }

/* MASTERY PATH */
.mastery-strip {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--rule);
  background: var(--paper);
}
.mastery-icon {
  width: 40px; height: 40px;
  background: var(--accent2);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: white;
}
.mastery-label {
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 600;
  margin-bottom: 3px;
}
.mastery-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--accent2);
  margin-bottom: 4px;
}
.mastery-detail {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.55;
}
.mastery-depth {
  padding: 20px 24px;
  border-bottom: 1px solid var(--rule);
}
.mastery-depth-label {
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 600;
  margin-bottom: 14px;
}
.mastery-bar-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.mastery-bar-label {
  font-size: 12px;
  color: var(--muted);
  width: 110px;
  flex-shrink: 0;
}
.mastery-bar-track {
  flex: 1;
  height: 7px;
  background: var(--rule);
  border-radius: 4px;
  overflow: hidden;
}
.mastery-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: var(--accent2);
  transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}
.mastery-bar-fill.median {
  background: repeating-linear-gradient(
    90deg,
    var(--muted) 0px, var(--muted) 3px,
    transparent 3px, transparent 8px
  );
}
.mastery-bar-value {
  font-size: 12px;
  color: var(--ink);
  font-weight: 500;
  width: 48px;
  text-align: right;
  flex-shrink: 0;
}
.mastery-pace-note {
  font-size: 12px;
  color: var(--muted);
  margin-top: 8px;
  line-height: 1.6;
  font-style: italic;
}
.pace-text.mastery { color: var(--accent2); }
`;

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────
function XPBar({ pct, label, nextLabel }) {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), 300); return () => clearTimeout(t); }, [pct]);
  return (
    <div className="xp-section">
      <div className="xp-labels"><span>{label}</span><span>{nextLabel}</span></div>
      <div className="xp-track"><div className="xp-fill" style={{ width: `${w}%` }} /></div>
    </div>
  );
}

function GapMeter({ label, value, sub }) {
  const [w, setW] = useState(0);
  const abs = Math.min(Math.abs(value), 100);
  const cls = value < 0 ? "neg" : "pos";
  useEffect(() => { const t = setTimeout(() => setW(abs), 500); return () => clearTimeout(t); }, [abs]);
  return (
    <div>
      <div className="gm-header">
        <span className="gm-label">{label}</span>
        <span className={`gm-value ${cls}`}>{value > 0 ? "+" : ""}{value}%</span>
      </div>
      <div className="gm-track"><div className={`gm-fill ${cls}`} style={{ width: `${w}%` }} /></div>
      {sub && <div className="gm-sub">{sub}</div>}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
async function exportToPDF(result, blsData, parsedLoc, salP25, salP50, salP75, sym, tierMeta) {
  const a = result.answers || {};
  const yearsExp = result.totalYears;
  const currentYear = new Date().getFullYear();
  const geoEntry = blsData.geoEntry || {};
  const eq = result.equityAnalysis || {};
  const btn = document.querySelector('.btn-pdf');
  const originalText = btn?.textContent;
  if (btn) {
    btn.textContent = 'Generating PDF…';
    btn.disabled = true;
  }
  try {
    // Build full payload including equity analysis
    const payload = {
      name: a.name || "Report",
      currentTitle: a.currentTitle || "",
      industry: a.industry || "",
      yearsExp: yearsExp,
      location: `${parsedLoc.city || ""}${parsedLoc.country ? ", " + parsedLoc.country : ""}`,
      currentLevelTitle: result.currentLevelTitle || blsData.band?.title || "",
      progressionPace: result.progressionPace || "on track",
      paceDetail: result.paceDetail || "",
      progressPct: result.progressPct || 50,
      standout: result.standout || "",
      summary: result.summary || "",
      topSkills: result.topSkills || [],
      nextSteps: result.nextSteps || [],
      realComparisons: result.realComparisons || [],
      salP25: salP25,
      salP50: salP50,
      salP75: salP75,
      currencySymbol: sym || "$",
      currency: geoEntry.currency || "USD",
      dataSource: geoEntry.source || "BLS OEWS 2023",
      tierLabel: tierMeta?.label || "",
      currentYear: currentYear,
      // Full equity analysis — this is what was missing
      equity: eq.hasFactors ? {
        hasFactors: true,
        salaryGapPct: eq.salaryGapPct || 0,
        adjustedSalary: eq.adjustedSalaryP50 || salP50,
        estimatedLevelWithoutBarriers: eq.trueTitle || "",
        callout: eq.callout || "",
        factors: (eq.factors || [])
          .filter(f => f.active)
          .map(f => ({
            label: f.name,
            icon: f.icon || "⚖",
            detail: `${f.stat || ""} — ${f.impact || ""}`.trim().replace(/^— |— $/, ""),
          })),
      } : null,
    };
    const response = await fetch("/api/generate-pdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errData = await response.json();
        throw new Error(errData.detail || `Server error ${response.status}`);
      }
      throw new Error(`Server error ${response.status}`);
    }
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/pdf")) {
      const text = await response.text();
      throw new Error(`Unexpected response: ${text.slice(0, 200)}`);
    }
    // Trigger download
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `career-gpx-${(a.name || "report").toLowerCase().replace(/\s+/g, "-")}-${currentYear}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("PDF export failed:", err);
    alert("PDF export failed: " + err.message + "\n\nPlease try again in a moment.");
  } finally {
    if (btn) {
      btn.textContent = originalText || "↓ Export PDF Report";
      btn.disabled = false;
    }
  }
}

// ─── PRICING MODAL ────────────────────────────────────────────────────────────
function PricingModal({ onClose, highlightTier = "equity" }) {
  return (
    <div className="pricing-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pricing-modal">
        <div className="pricing-header">
          <div>
            <div className="pricing-title">Unlock the full picture</div>
            <div className="pricing-sub">Choose the plan that fits how you work</div>
          </div>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="pricing-grid">
          {PRICING_TIERS.map(tier => (
            <div key={tier.id} className={`pricing-card ${tier.highlight ? "highlighted" : ""}`}>
              <div className="tier-name">{tier.name}</div>
              <div className="tier-price">{tier.price}</div>
              <div className="tier-period">{tier.period || " "}</div>
              <ul className="tier-features">
                {tier.features.map((f, i) => (
                  <li key={i} className="tier-feature">{f}</li>
                ))}
              </ul>
              {tier.stripeLink ? (
                <button className="btn-tier" onClick={() => window.open(STRIPE_LINKS[tier.stripeLink], '_blank')}>
                  {tier.cta}
                </button>
              ) : (
                <button className="btn-tier" style={{ opacity: 0.4, cursor: "default" }}>{tier.cta}</button>
              )}
            </div>
          ))}
        </div>
        <div className="pricing-footer">
          Secure payment via Stripe · Cancel anytime · Questions? info@tkd-consulting.com
        </div>
      </div>
    </div>
  );
}

export default function CareerGPX() {
  const [step, setStep] = useState(0); // 0..N = wizard steps, -1 = loading, -2 = result
  const [answers, setAnswers] = useState({});
  const [currentInput, setCurrentInput] = useState("");
  const [resumeTab, setResumeTab] = useState("upload");
  const [fileName, setFileName] = useState("");
  const [locCountry, setLocCountry] = useState("");
  const [locCity, setLocCity] = useState("");
  const [isProMode, setIsProMode] = useState(false);
  const [equityPaid, setEquityPaid] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [proStep, setProStep] = useState(0); // index into PRO_QUESTIONS
  const [proAnswers, setProAnswers] = useState({});
  const [proInput, setProInput] = useState("");
  const [showPricing, setShowPricing] = useState(false);
  const fileRef = useRef();
  const [loadStep, setLoadStep] = useState(0);
  const [result, setResult] = useState(null);
  const [blsData, setBlsData] = useState(null);
  const [error, setError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [lastAnswers, setLastAnswers] = useState(null); // for retry

  const loadingSourceLabel = (() => {
    try {
      const loc = JSON.parse(answers.location || "{}");
      return getDataSourceLabel(loc.country);
    } catch { return { name: "BLS", dataYear: 2023 }; }
  })();

  const LOAD_STEPS = [
    { icon: "📊", text: `Looking up ${loadingSourceLabel.name} ${loadingSourceLabel.dataYear} benchmark data...` },
    { icon: "🔍", text: "Assessing career progression..." },
    { icon: "💰", text: "Calibrating salary ranges..." },
    { icon: "👥", text: "Finding real-world comparisons..." },
    { icon: "⚖️", text: "Running equity gap analysis..." },
  ];

  const currentQ = QUESTIONS[step];
  const totalSteps = QUESTIONS.length;
  const progress = step >= 0 ? ((step) / totalSteps) * 100 : 100;

  // Pre-fill current input from answers when navigating back
  useEffect(() => {
    if (step >= 0 && step < QUESTIONS.length) {
      const q = QUESTIONS[step];
      if (q.type === "multicheck") {
        setCurrentInput(answers[q.id] || []);
      } else {
        setCurrentInput(answers[q.id] || "");
      }
    }
  }, [step]);

  // Restore result + handle payment confirmation after Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentType = params.get("payment_type");
    if (!sessionId) return;

    // Restore saved report first — try sessionStorage, fall back to localStorage backup
    let restoredResult = null;
    try {
      const saved = sessionStorage.getItem("cgpx_result") || localStorage.getItem("cgpx_result") || localStorage.getItem("cgpx_result_backup");
      if (saved) {
        restoredResult = JSON.parse(saved);
        setResult(restoredResult);
        setStep(-2);
      }
    } catch {}

    // Then poll for payment confirmation
    setCheckingPayment(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const res = await fetch(`/api/check-payment?sessionId=${sessionId}`);
        const data = await res.json();
        if (data.paid) {
          if (paymentType === "equityUnlock" || data.type === "equityUnlock") {
            try { localStorage.setItem("cgpx_equity_paid", "true"); } catch {}
            setEquityPaid(true);
            setCheckingPayment(false);
            setStep(-2);
            try { localStorage.removeItem("cgpx_result_backup"); } catch {}
            window.history.replaceState({}, "", window.location.pathname);
          } else if (paymentType === "pro" || data.type === "pro") {
            setIsProMode(true);
            setCheckingPayment(false);
            // Go into Pro questions flow instead of showing report
            setStep(9999);
            setProStep(0);
            setProInput("");
            window.history.replaceState({}, "", window.location.pathname);
          }
        } else if (attempts < 30) {
          setTimeout(poll, 1000);
        } else {
          setCheckingPayment(false);
          if (restoredResult) setStep(-2);
          console.warn("Payment not confirmed after 30s");
        }
      } catch (err) {
        setCheckingPayment(false);
        if (restoredResult) setStep(-2);
        console.error("Payment check error:", err);
      }
    };
    poll();
  }, []);

  const canAdvance = () => {
    if (!currentQ) return false;
    if (currentQ.optional) return true;
    if (currentQ.type === "multicheck") return true;
    if (currentQ.type === "locationPicker") return locCity.length > 0;
    if (currentQ.type === "resumeOrText") return fileName.length > 0 || String(currentInput).trim().length > 0;
    if (currentQ.id === "startYear") {
      const yr = parseInt(currentInput);
      const thisYear = new Date().getFullYear();
      if (isNaN(yr) || yr > thisYear || yr < 1950) return false;
    }
    return String(currentInput).trim().length > 0;
  };

  const startYearError = (() => {
    if (currentQ?.id !== "startYear" || !currentInput) return null;
    const yr = parseInt(currentInput);
    const thisYear = new Date().getFullYear();
    if (isNaN(yr)) return null;
    if (yr > thisYear) return `That's in the future — please enter a year up to ${thisYear}.`;
    if (yr < 1950) return "Please enter a year after 1950.";
    return null;
  })();

  const advancePro = () => {
    const q = PRO_QUESTIONS[proStep];
    const val = String(proInput).trim();
    setProAnswers(prev => ({ ...prev, [q.id]: val }));
    if (proStep < PRO_QUESTIONS.length - 1) {
      setProStep(proStep + 1);
      setProInput("");
    } else {
      // Done with pro questions — run full analysis
      runAnalysis({ ...answers, ...proAnswers, [q.id]: val });
    }
  };

  const runAnalysis = async (allAnswers) => {
    setStep(-1);
    setLoadStep(0);
    setError("");
    setLastAnswers(allAnswers);
    const currentYear = new Date().getFullYear();
    const yearsExp = currentYear - parseInt(allAnswers.startYear || currentYear);
    const bls = getBLSBenchmark(allAnswers.industry, yearsExp);
    let parsedLoc = { country: "United States", city: "Other US City" };
    try { parsedLoc = JSON.parse(allAnswers.location || "{}"); } catch(e) {}
    const geoEntry = getGeoEntry(parsedLoc.country, parsedLoc.city);
    bls.geoEntry = geoEntry;
    setBlsData(bls);
    for (let i = 0; i < LOAD_STEPS.length; i++) {
      await new Promise(r => setTimeout(r, 900));
      setLoadStep(i + 1);
    }
    try {
      const data = await analyzeCareer(allAnswers, bls); // has built-in retry
      data.totalYears = currentYear - parseInt(allAnswers.startYear);
      setResult({ ...data, answers: allAnswers, bls, isProReport: isProMode });
      try { sessionStorage.setItem("cgpx_result", JSON.stringify({ ...data, answers: allAnswers, bls, isProReport: isProMode })); } catch {}
      try { localStorage.setItem("cgpx_result", JSON.stringify({ ...data, answers: allAnswers, bls, isProReport: isProMode })); } catch {}
      setStep(-2);
      // Fire Plausible custom event for report generation tracking
      try {
        if (typeof window.plausible === "function") {
          window.plausible("Report Generated");
        }
      } catch (e) {
        // Silently ignore if Plausible not loaded
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Analysis failed — please try again.");
      setStep(-3); // error state
    }
  };

  const advance = async () => {
    const q = currentQ;
    const val = q.type === "multicheck" ? (currentInput || []) : String(currentInput).trim();
    const newAnswers = { ...answers, [q.id]: val };
    setAnswers(newAnswers);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else if (isProMode) {
      // Go into Pro questions
      setStep(9999); // sentinel: "in pro wizard"
      setProStep(0);
      setProInput("");
    } else {
      await runAnalysis(newAnswers);
    }
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  const restart = () => {
    setStep(0); setAnswers({}); setCurrentInput(""); setResult(null); setLoadStep(0); setError(""); setFileName(""); setResumeTab("upload"); setLocCountry(""); setLocCity(""); setProStep(0); setProAnswers({}); setProInput(""); setShowPricing(false); setEquityPaid(false);
    try { sessionStorage.removeItem("cgpx_result"); } catch {}
    try { localStorage.removeItem("cgpx_result"); } catch {}
    try { localStorage.removeItem("cgpx_result_backup"); } catch {}
    try { localStorage.removeItem("cgpx_equity_paid"); } catch {}
  };

  const renderInput = () => {
    const q = currentQ;
    if (!q) return null;

    if (q.type === "text" || q.type === "year") {
      return (
        <>
          <input
            className="wizard-input"
            type={q.type === "year" ? "number" : "text"}
            placeholder={q.placeholder}
            value={currentInput}
            onChange={e => setCurrentInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && canAdvance() && advance()}
            autoFocus
            min={q.type === "year" ? 1950 : undefined}
            max={q.type === "year" ? new Date().getFullYear() : undefined}
          />
          {startYearError && (
            <div style={{ color: "var(--accent)", fontSize: 13, marginTop: 8, fontWeight: 500 }}>
              ⚠ {startYearError}
            </div>
          )}
        </>
      );
    }

    if (q.type === "locationPicker") {
      const countries = Object.keys(GEO_DATA);
      if (!locCountry) {
        return (
          <div>
            <div className="loc-step-label">Step 1 — Select your country or region</div>
            <div className="loc-countries">
              {countries.map(c => (
                <button key={c} className="loc-country-btn" onClick={() => setLocCountry(c)}>
                  <span className="loc-flag">{GEO_DATA[c].flag}</span>
                  {c}
                </button>
              ))}
            </div>
          </div>
        );
      }
      const cities = GEO_DATA[locCountry]?.cities || [];
      return (
        <div>
          <div className="loc-step-label">Step 2 — Select your city</div>
          <button className="loc-back-link" onClick={() => { setLocCountry(""); setLocCity(""); setCurrentInput(""); }}>
            ← {locCountry}
          </button>
          <div className="loc-cities">
            {cities.map(c => (
              <button
                key={c.name}
                className={`loc-city-btn ${locCity === c.name ? "selected" : ""}`}
                onClick={() => {
                  setLocCity(c.name);
                  setCurrentInput(JSON.stringify({ country: locCountry, city: c.name }));
                }}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (q.type === "textarea") {
      return (
        <textarea
          className="wizard-textarea"
          placeholder={q.placeholder}
          value={currentInput}
          onChange={e => setCurrentInput(e.target.value)}
          autoFocus
          rows={5}
        />
      );
    }

    if (q.type === "resumeOrText") {
      const handleFile = (file) => {
        if (!file) return;
        setFileName(file.name);
        const ext = file.name.split('.').pop().toLowerCase();
        // PDF and docx are binary — readAsText gives garbage. Use filename only.
        if (ext === "pdf" || ext === "docx" || ext === "doc") {
          setCurrentInput(`[Resume uploaded: ${file.name} — please describe your background in the text box below for best results]`);
          setResumeTab("type");
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          // Hard cap at 4000 chars (~1000 tokens) to stay well within API limits
          const raw = ev.target.result || "";
          const truncated = raw.length > 4000 ? raw.slice(0, 4000) + "\n[truncated for length]" : raw;
          setCurrentInput(truncated);
        };
        reader.readAsText(file);
      };
      return (
        <div>
          <div className="dual-tabs">
            <button className={`dual-tab ${resumeTab === "upload" ? "active" : ""}`} onClick={() => setResumeTab("upload")}>
              📄 Upload Resume
            </button>
            <button className={`dual-tab ${resumeTab === "type" ? "active" : ""}`} onClick={() => setResumeTab("type")}>
              ✏️ Type it out
            </button>
          </div>
          {resumeTab === "upload" ? (
            <div
              className={`upload-zone ${fileName ? "has-file" : ""}`}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
            >
              <span className="upload-icon">{fileName ? "✅" : "📎"}</span>
              {fileName ? (
                <>
                  <div className="upload-label">Resume uploaded</div>
                  <div className="upload-badge">📄 {fileName}</div>
                  <div className="upload-sub" style={{marginTop:10}}>Click to replace</div>
                </>
              ) : (
                <>
                  <div className="upload-label">Drop your resume here</div>
                  <div className="upload-sub">PDF, Word, or plain text · Click to browse</div>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".txt,.pdf,.doc,.docx"
                style={{ display: "none" }}
                onChange={e => handleFile(e.target.files?.[0])}
              />
            </div>
          ) : (
            <textarea
              className="wizard-textarea"
              placeholder={q.placeholder}
              value={typeof currentInput === "string" && !fileName ? currentInput : ""}
              onChange={e => { setCurrentInput(e.target.value); setFileName(""); }}
              autoFocus
              rows={6}
            />
          )}
        </div>
      );
    }

    if (q.type === "select") {
      // Use a native dropdown for long lists (industry), pill grid for short ones
      if (q.options.length > 8) {
        return (
          <select
            value={currentInput || ""}
            onChange={e => setCurrentInput(e.target.value)}
            style={{
              width: "100%", padding: "14px 16px",
              fontFamily: "var(--font-body)", fontSize: 15,
              border: "1.5px solid var(--rule)", borderRadius: "var(--r)",
              background: "var(--paper)", color: currentInput ? "var(--ink)" : "var(--muted)",
              appearance: "none", cursor: "pointer",
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center",
            }}
            autoFocus
          >
            <option value="" disabled>Select your industry…</option>
            {q.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      }
      return (
        <div className="option-grid">
          {q.options.map(opt => (
            <button
              key={opt}
              className={`option-btn ${currentInput === opt ? "selected" : ""}`}
              onClick={() => setCurrentInput(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }

    if (q.type === "multicheck") {
      const selected = Array.isArray(currentInput) ? currentInput : [];
      return (
        <div className="check-grid">
          {q.options.map(opt => {
            const isChecked = selected.includes(opt.value);
            return (
              <div
                key={opt.value}
                className={`check-item ${isChecked ? "checked" : ""}`}
                onClick={() => setCurrentInput(prev => {
                  const arr = Array.isArray(prev) ? prev : [];
                  return isChecked ? arr.filter(v => v !== opt.value) : [...arr, opt.value];
                })}
              >
                <div className="check-dot">{isChecked ? "✓" : ""}</div>
                {opt.label}
              </div>
            );
          })}
        </div>
      );
    }
  };

  // ── PRO WIZARD ──
  if (step === 9999) {
    const q = PRO_QUESTIONS[proStep];
    const canProAdvance = q.type === "select"
      ? String(proInput).trim().length > 0
      : String(proInput).trim().length > 0;
    return (
      <>
        <style>{CSS}</style>
        <div className="app">
          <div className="masthead">
            <div className="masthead-title">Career GPX</div>
            <div className="masthead-rule" />
            <span className="pro-badge">✦ Pro</span>
          </div>
          <div className="progress-wrap">
            <div className="progress-fill" style={{ width: `${((proStep + 1) / PRO_QUESTIONS.length) * 100}%` }} />
          </div>
          <div className="wizard">
            <div className="wizard-step">Pro question {proStep + 1} of {PRO_QUESTIONS.length}</div>
            <div className="wizard-q">{q.question}</div>
            <div className="wizard-sub" dangerouslySetInnerHTML={{ __html: q.subtext }} />
            {q.type === "salary" ? (
              <div className="salary-input-wrap">
                <span className="salary-prefix">$</span>
                <input className="wizard-input" type="number" placeholder={q.placeholder}
                  value={proInput} onChange={e => setProInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && canProAdvance && advancePro()} autoFocus />
              </div>
            ) : q.type === "select" ? (
              <div className="option-grid">
                {q.options.map(opt => (
                  <button key={opt} className={`option-btn ${proInput === opt ? "selected" : ""}`}
                    onClick={() => setProInput(opt)}>{opt}</button>
                ))}
              </div>
            ) : (
              <input className="wizard-input" type="text" placeholder={q.placeholder}
                value={proInput} onChange={e => setProInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && canProAdvance && advancePro()} autoFocus />
            )}
            <div className="wizard-nav">
              {proStep > 0 && <button className="btn-back" onClick={() => { setProStep(proStep - 1); setProInput(Object.values(proAnswers)[proStep - 1] || ""); }}>← Back</button>}
              <button className="btn-next" disabled={!canProAdvance} onClick={advancePro}>
                {proStep === PRO_QUESTIONS.length - 1 ? "Generate Pro Report →" : "Continue →"}
              </button>
              <button className="skip-btn" onClick={advancePro}>Skip</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── WIZARD ──
  if (step >= 0 && step < QUESTIONS.length) {
    const q = QUESTIONS[step];
    return (
      <>
        <style>{CSS}</style>
        <div className="app">
          <div className="masthead">
            <div className="masthead-title">Career GPX</div>
            <div className="masthead-rule" />
            <div className="masthead-sub">Career Intelligence Report</div>
          </div>
          <div className="progress-wrap">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="wizard">
            {step === 0 && (
              <div style={{ marginBottom: 28, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  onClick={() => setIsProMode(false)}
                  style={{ padding: "8px 16px", border: `1.5px solid ${!isProMode ? "var(--ink)" : "var(--rule)"}`, borderRadius: "var(--r)", background: !isProMode ? "var(--ink)" : "transparent", color: !isProMode ? "var(--paper)" : "var(--muted)", fontFamily: "var(--font-body)", fontSize: 13, cursor: "pointer", fontWeight: 500 }}
                >Free Report</button>
                <button
                  onClick={() => window.open(STRIPE_LINKS.pro, '_blank')}
                  style={{ padding: "8px 16px", border: `1.5px solid var(--rule)`, borderRadius: "var(--r)", background: "transparent", color: "var(--muted)", fontFamily: "var(--font-body)", fontSize: 13, cursor: "pointer", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}
                ><span className="pro-badge" style={{ fontSize: 9, padding: "2px 7px" }}>✦ Pro</span> Full Report — $19/mo</button>
              </div>
            )}
            <div className="wizard-step">Question {step + 1} of {totalSteps}</div>
            <div className="wizard-q">{q.question}</div>
            <div className="wizard-sub" dangerouslySetInnerHTML={{ __html: q.subtext }} />
            {renderInput()}
            {error && step !== -3 && <div style={{ color: "var(--accent)", fontSize: 13, marginTop: 12 }}>⚠ {error}</div>}

            {/* T&C checkbox — shown only on final step */}
            {step === QUESTIONS.length - 1 && (
              <div style={{
                marginTop: 20,
                padding: "14px 16px",
                background: "rgba(10,10,15,0.03)",
                border: "1px solid var(--rule)",
                borderRadius: "var(--r)",
              }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={e => setAgreedToTerms(e.target.checked)}
                    style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--ink)", cursor: "pointer", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
                    I have read and agree to Career GPX's{" "}
                    <a href="https://careergpx.com/terms" target="_blank" rel="noopener noreferrer"
                       style={{ color: "var(--ink)", textDecoration: "underline", textUnderlineOffset: 3 }}>
                      Terms of Service
                    </a>{" "}and{" "}
                    <a href="https://careergpx.com/privacy" target="_blank" rel="noopener noreferrer"
                       style={{ color: "var(--ink)", textDecoration: "underline", textUnderlineOffset: 3 }}>
                      Privacy Policy
                    </a>.
                    {" "}<span style={{ color: "var(--muted)", fontSize: 11 }}>
                      This tool provides informational benchmarks only — not financial or career advice.
                    </span>
                  </span>
                </label>
              </div>
            )}
            <div className="wizard-nav">
              {step > 0 && <button className="btn-back" onClick={back}>← Back</button>}
              <button className="btn-next"
                disabled={!canAdvance() || (step === QUESTIONS.length - 1 && !agreedToTerms)}
                onClick={advance}
                style={step === QUESTIONS.length - 1 && !agreedToTerms ? { opacity: 0.45, cursor: "not-allowed" } : {}}
              >
                {step === QUESTIONS.length - 1 ? (isProMode ? "Continue to Pro Questions →" : "Generate My Report →") : "Continue →"}
              </button>
              {q.optional && (
                <button className="skip-btn" onClick={advance}>Skip</button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── LOADING ──
  if (step === -1) {
    return (
      <>
        <style>{CSS}</style>
        <div className="app">
          <div className="masthead">
            <div className="masthead-title">Career GPX</div>
            <div className="masthead-rule" />
            <div className="masthead-sub">Generating Report</div>
          </div>
          <div className="progress-wrap"><div className="progress-fill" style={{ width: "100%" }} /></div>
          <div className="loading-wrap">
            <div className="loading-spinner" />
            <div className="loading-title">Building your report</div>
            <div style={{ fontSize: 14, color: "var(--muted)", marginTop: 6 }}>Benchmarking against real {loadingSourceLabel.name} {loadingSourceLabel.dataYear} data...</div>
            <div className="loading-steps">
              {LOAD_STEPS.map((s, i) => (
                <div key={i} className={`loading-step ${loadStep === i + 1 ? "active" : loadStep > i + 1 ? "done" : ""}`}>
                  <span className="step-icon">{loadStep > i ? "✓" : s.icon}</span>
                  {s.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── ERROR ──
  if (step === -3) {
    return (
      <>
        <style>{CSS}</style>
        <div className="app">
          <div className="masthead">
            <div className="masthead-title">Career GPX</div>
            <div className="masthead-rule" />
            <div className="masthead-sub">Something went wrong</div>
          </div>
          <div style={{ maxWidth: 480, margin: "60px auto", padding: "0 24px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 20 }}>⚠️</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 700, marginBottom: 12 }}>
              Analysis couldn't complete
            </div>
            <div style={{ fontSize: 15, color: "var(--muted)", lineHeight: 1.7, marginBottom: 8 }}>
              {error || "The AI analysis failed after 3 attempts."}
            </div>
            <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6, marginBottom: 32, padding: "12px 16px", background: "var(--paper2)", borderRadius: "var(--r)", border: "1px solid var(--rule)" }}>
              {error?.includes("too long") 
                ? "Your resume or context was too large to process. Try typing a short summary instead — 3–5 sentences is plenty."
                : "This is usually a temporary issue. Your answers are saved — hit retry and it should work."}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                className="btn-next"
                onClick={() => lastAnswers && runAnalysis(lastAnswers)}
                style={{ minWidth: 160 }}
              >
                ↺ Retry Analysis
              </button>
              <button
                className="btn-back"
                onClick={() => { setStep(0); setError(""); }}
              >
                ← Start Over
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--muted)" }}>
              Sorry, it's not working right now — please try again later.
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── RESULT ──
  // Show payment checking screen
  if (checkingPayment) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "var(--paper)", fontFamily: "var(--font-body)" }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 22, fontFamily: "var(--font-head)", fontWeight: 700, marginBottom: 8 }}>Confirming your payment…</div>
        <div style={{ fontSize: 14, color: "var(--muted)" }}>This takes just a moment. Please don't close this tab.</div>
      </div>
    );
  }

  if (step === -2 && result) {
    const { answers: a, bls } = result;
    const currentYear = new Date().getFullYear();
    const yearsExp = result.totalYears;
    // Parse stored location JSON
    let parsedLoc = { country: "United States", city: "Other US City" };
    try { parsedLoc = JSON.parse(a.location || "{}"); } catch(e) {}
    const geoEntry = bls.geoEntry || getGeoEntry(parsedLoc.country, parsedLoc.city);
    const sourceLabelObj = getDataSourceLabel(parsedLoc.country);
    const sourceLabel = sourceLabelObj.name;
    const sourceYear = sourceLabelObj.dataYear;
    const tierMeta = TIER_META[geoEntry.tier] || TIER_META[1];
    const salP25 = applyGeo(bls.band.p25, geoEntry);
    const salP50 = applyGeo(bls.band.p50, geoEntry);
    const salP75 = applyGeo(bls.band.p75, geoEntry);
    const sym = geoEntry.symbol || "$";
    const eq = result.equityAnalysis;
    const identityFactors = a.identity || [];
    const hasEquity = eq?.hasFactors && identityFactors.length > 0;
    const isProReport = result.isProReport || false;
    const equityUnlocked = isProReport || equityPaid;

    const isMastery = result.progressionPace === "mastery";
    const paceIcon = result.progressionPace === "ahead" ? "🚀" : result.progressionPace === "behind" ? "📈" : result.progressionPace === "mastery" ? "♥" : "✓";
    const paceClass = result.progressionPace === "ahead" ? "ahead" : result.progressionPace === "behind" ? "behind" : result.progressionPace === "mastery" ? "mastery" : "";

    return (
      <>
        <style>{CSS}</style>
        <div className="app">
          <div className="masthead">
            <div className="masthead-title">Career GPX</div>
            <div className="masthead-rule" />
            <div className="masthead-sub">Your Career Intelligence Report</div>
          </div>
          <div className="progress-wrap"><div className="progress-fill" style={{ width: "100%" }} /></div>

          {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
          <div className="result-outer">
            <div className="result-card">

              {/* HEADER */}
              <div className="card-header">
                <div className="card-header-eyebrow">Career Intelligence Report · {currentYear}</div>
                <div className="card-name">{a.name}</div>
                <div className="card-headline">
                  <div className="card-level-badge">{result.currentLevelTitle || bls.band.title}</div>
                  <div className="card-title-text">{a.currentTitle} · {yearsExp} years experience · {a.industry}</div>
                </div>
              </div>

              {/* PACE */}
              {result.progressionPace !== "mastery" && (
              <div className="pace-strip">
                <div className="pace-icon">{paceIcon}</div>
                <div>
                  <div className="pace-label">Progression Pace</div>
                  <div className={`pace-text ${paceClass}`}>
                    {result.progressionPace === "ahead" ? "Ahead of typical pace" : result.progressionPace === "behind" ? "Behind typical pace" : "On track"}
                  </div>
                </div>
                <div className="pace-detail">{result.paceDetail}</div>
              </div>
              )}

              {/* XP BAR / MASTERY BAR */}
              {isMastery ? (
                <>
                  {/* Mastery pace strip — replaces the standard pace strip */}
                  <div className="mastery-strip">
                    <div className="mastery-icon">♥</div>
                    <div>
                      <div className="mastery-label">Progression Pace</div>
                      <div className="mastery-title">Mastery path</div>
                      <div className="mastery-detail">{result.paceDetail}</div>
                    </div>
                  </div>

                  {/* Experience depth bars */}
                  <div className="mastery-depth">
                    <div className="mastery-depth-label">Experience Depth</div>
                    <div className="mastery-bar-row">
                      <span className="mastery-bar-label">Your depth</span>
                      <div className="mastery-bar-track">
                        <div
                          className="mastery-bar-fill"
                          style={{ width: `${Math.min(Math.round((result.totalYears / 22) * 100), 96)}%` }}
                        />
                      </div>
                      <span className="mastery-bar-value">{result.totalYears} years</span>
                    </div>
                    <div className="mastery-bar-row">
                      <span className="mastery-bar-label">Industry median</span>
                      <div className="mastery-bar-track">
                        <div className="mastery-bar-fill median" style={{ width: "38%" }} />
                      </div>
                      <span className="mastery-bar-value" style={{ color: "var(--muted)" }}>~7 yrs</span>
                    </div>
                    <div className="mastery-pace-note">{result.paceDetail}</div>
                  </div>
                </>
              ) : (
                <XPBar
                  pct={result.progressPct || 50}
                  label={result.currentLevelTitle || bls.band.title}
                  nextLabel={bls.allBands[Math.min(result.currentLevelNum || 2, bls.allBands.length - 1)]?.title || "Next Level"}
                />
              )}

              {/* STANDOUT */}
              {result.standout && (
                <div style={{ padding: "16px 36px", borderBottom: "1px solid var(--rule)", fontSize: 14, color: "var(--ink2)", lineHeight: 1.7, fontStyle: "italic" }}>
                  "{result.standout}"
                </div>
              )}

              {/* TWO COL: Salary + Skills */}
              <div className="card-body">
                <div className="card-col">
                  <div className="col-label">Market Salary Range</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.55, padding: "8px 10px", background: "var(--paper2)", borderRadius: "var(--r)", borderLeft: "3px solid var(--rule)" }}>
                    <strong style={{color:"var(--ink)"}}>ℹ️ What this shows:</strong> What people at your level typically earn — not your personal salary. Population-level data for {a.industry} in {parsedLoc.city}, {parsedLoc.country}.
                  </div>
                  <div className="salary-row">
                    <span className="sal-label">25th percentile earners</span>
                    <span className="sal-value">{formatSalary(salP25, sym)}</span>
                  </div>
                  <div className="salary-row">
                    <span className="sal-label">Median (typical market rate)</span>
                    <span className="sal-value target">{formatSalary(salP50, sym)}</span>
                  </div>
                  <div className="salary-row">
                    <span className="sal-label">75th percentile earners</span>
                    <span className="sal-value">{formatSalary(salP75, sym)}</span>
                  </div>
                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: tierMeta.color }}>{tierMeta.label}</span>
                  </div>
                  <div className="data-vintage">
                    Salary data: {sourceLabel} {sourceYear} · {tierMeta.label}
                  </div>
                  <div className="sal-source">
                    {geoEntry.source}.<br />
                    {geoEntry.currency !== "USD" && `Converted from USD at approximate market rate. `}
                    Figures in {geoEntry.currency}.
                  </div>
                </div>
                <div className="card-col">
                  <div className="col-label">Inferred Skills</div>
                  <div className="skill-list">
                    {(result.topSkills || []).map((s, i) => (
                      <span key={i} className="skill-tag" style={{ animationDelay: `${i * 70}ms` }}>{s}</span>
                    ))}
                  </div>
                  {result.summary && (
                    <div style={{ marginTop: 16, fontSize: 13, color: "var(--muted)", lineHeight: 1.65 }}>
                      {result.summary}
                    </div>
                  )}
                </div>
              </div>

              {/* REAL COMPARISONS */}
              {result.realComparisons?.length > 0 && (
                <div className="comparisons-section">
                  <div className="col-label" style={{ marginBottom: 8 }}>People Who Walked a Similar Path</div>
                  {result.realComparisons.map((c, i) => (
                    <div key={i} className="comparison-item">
                      <div className="comp-avatar">{c.initials || c.name?.slice(0,2).toUpperCase()}</div>
                      <div>
                        <div className="comp-name">{c.name}</div>
                        <div className="comp-known">{c.knownFor}</div>
                        <div className="comp-text">{c.atYourStage}</div>
                        {c.tag && <span className="comp-tag">{c.tag}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* EQUITY SECTION */}
              {identityFactors.length > 0 && (
                <div className={`equity-section ${!equityUnlocked ? "equity-locked" : ""}`}>

                  {/* EQUITY CONTENT — faded when locked */}
                  <div className={!equityUnlocked ? "equity-fade-wrap" : ""}>
                    <div className="equity-header">
                      <div className="equity-icon-wrap">⚖️</div>
                      <div>
                        <div className="equity-label">Research-Based Gap Estimate</div>
                        <div className="equity-headline">How systemic factors typically affect people like you</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6, padding: "12px 14px", background: "rgba(230,57,70,0.04)", border: "1px solid rgba(230,57,70,0.15)", borderRadius: "var(--r)", marginBottom: 20 }}>
                      <strong style={{color:"var(--ink)"}}>ℹ️ How this works:</strong> We don't know your actual salary. These figures apply published research findings (e.g. McKinsey's gender pay gap data, {sourceLabel} wage statistics) to the {sourceLabel} market rate above. They show what research says typically happens to people sharing your demographic profile — not a personal audit of your pay.
                    </div>

                    {eq?.trueTitle && (
                      <div className="level-compare">
                        <div className="lc-box actual">
                          <div className="lc-label">BLS-Benchmarked Level</div>
                          <div className="lc-title">{result.currentLevelTitle || bls.band?.title}</div>
                        </div>
                        <div className="lc-arrow">→</div>
                        <div className="lc-box true">
                          <div className="lc-label">Estimated Level Without Barriers</div>
                          <div className="lc-title">{eq.trueTitle}</div>
                        </div>
                      </div>
                    )}

                    <div className="gap-meters" style={{ marginTop: 20 }}>
                      {typeof eq?.salaryGapPct === "number" && eq.salaryGapPct !== 0 && (
                        <GapMeter
                          label="Typical salary gap for people with your profile vs. equivalent peer"
                          value={eq.salaryGapPct}
                          sub={`Research-adjusted market rate: ${formatSalary(eq.adjustedSalaryP50, sym)} vs. ${formatSalary(salP50, sym)} median for a demographically neutral peer at the same level`}
                        />
                      )}
                    </div>

                    {eq?.factors?.length > 0 && (
                      <>
                        <div className="col-label" style={{ marginBottom: 10 }}>Factor Breakdown</div>
                        <div className="equity-factors">
                          {eq.factors.map((f, i) => (
                            <div key={i} className={`ef-row ${f.active ? "active" : "inactive"}`}>
                              <span className="ef-icon">{f.icon}</span>
                              <div>
                                <div className="ef-name">{f.name}</div>
                                <div className="ef-stat">{f.stat} — {f.impact}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {eq?.callout && (
                      <div className="equity-callout">
                        <div className="callout-label">Research Says — Not Your Personal Situation</div>
                        <div className="callout-text">{eq.callout}</div>
                      </div>
                    )}
                  </div>

                  {/* PAYWALL — news-style, shown below fade */}
                  {!equityUnlocked && (() => {
                    // Format identity factors as natural, readable text
                    const FACTOR_LABELS = {
                      "woman": "being a woman",
                      "person of color": "being a person of color",
                      "first-generation professional": "being a first-generation professional",
                      "career gap": "having a career gap",
                      "LGBTQ+": "being LGBTQ+",
                      "immigrant": "being an immigrant",
                      "non-hub location": "living outside a major hub city",
                    };
                    const labels = identityFactors.map(f => FACTOR_LABELS[f] || f);
                    const factorText = labels.length === 1
                      ? labels[0]
                      : labels.length === 2
                        ? `${labels[0]} and ${labels[1]}`
                        : `${labels.slice(0,-1).join(", ")}, and ${labels[labels.length-1]}`;

                    // Show headline gap number for free — the uncomfortable truth lands free
                    const headlineGap = eq?.salaryGapPct;
                    const hasGap = typeof headlineGap === "number" && headlineGap !== 0;

                    return (
                      <div className="equity-paywall">
                        {/* Free teaser — headline number, no breakdown */}
                        {hasGap && (
                          <div className="equity-headline-free">
                            <div className="equity-headline-free-label">Research estimate for your profile</div>
                            <div className="equity-headline-free-stat">
                              People with your profile typically face a {Math.abs(headlineGap)}% salary gap
                            </div>
                            <div className="equity-headline-free-sub">
                              That's the headline. The full breakdown — factor by factor, with adjusted salary figures and what to do about it — is below.
                            </div>
                          </div>
                        )}

                        <div className="lock-icon">🔒</div>
                        <div className="lock-title" style={{ marginBottom: 8 }}>
                          See the full picture
                        </div>
                        <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.6, marginBottom: 16, textAlign: "center", maxWidth: 340, margin: "0 auto 16px" }}>
                          We've built a detailed breakdown of how {
                            labels.length === 1
                              ? `being ${identityFactors[0] === "career gap" ? "someone with a career gap" : identityFactors[0] === "non-hub location" ? "someone living outside a major hub city" : `a ${identityFactors[0]}`}`
                              : `being ${labels.slice(0,-1).map((l,i) => identityFactors[i] === "career gap" ? "having a career gap" : identityFactors[i] === "non-hub location" ? "living outside a major hub city" : `a ${identityFactors[i]}`).join(", ")}${labels.length > 1 ? `, and ${identityFactors[identityFactors.length-1] === "career gap" ? "having a career gap" : identityFactors[identityFactors.length-1] === "non-hub location" ? "living outside a major hub city" : `being a ${identityFactors[identityFactors.length-1]}`}` : ""}`
                          } typically affects pay and progression in {answers.industry} — based on McKinsey, {sourceLabel}, and peer-reviewed research.
                        </div>
                        <button className="btn-unlock" onClick={async () => {
                          try { sessionStorage.setItem("cgpx_result", JSON.stringify(result)); } catch {}
                          try { localStorage.setItem("cgpx_result_backup", JSON.stringify(result)); } catch {}

                          // Generate a random token to link this report to the Stripe session
                          const token = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

                          const reportPayload = {
                            name: a.name || "",
                            currentTitle: a.currentTitle || "",
                            industry: a.industry || "",
                            yearsExp: yearsExp,
                            location: `${parsedLoc.city || ""}${parsedLoc.country ? ", " + parsedLoc.country : ""}`,
                            currentLevelTitle: result.currentLevelTitle || bls.band?.title || "",
                            progressionPace: result.progressionPace || "on track",
                            paceDetail: result.paceDetail || "",
                            progressPct: result.progressPct || 50,
                            standout: result.standout || "",
                            summary: result.summary || "",
                            topSkills: result.topSkills || [],
                            nextSteps: result.nextSteps || [],
                            realComparisons: result.realComparisons || [],
                            salP25: salP25,
                            salP50: salP50,
                            salP75: salP75,
                            currencySymbol: sym || "$",
                            currency: geoEntry.currency || "USD",
                            dataSource: geoEntry.source || "BLS OEWS 2023",
                            currentYear: new Date().getFullYear(),
                            equity: (() => {
                              const eq = result.equityAnalysis || {};
                              if (!eq.hasFactors) return null;
                              return {
                                hasFactors: true,
                                salaryGapPct: eq.salaryGapPct || 0,
                                adjustedSalary: eq.adjustedSalaryP50 || salP50,
                                estimatedLevelWithoutBarriers: eq.trueTitle || "",
                                callout: eq.callout || "",
                                factors: (eq.factors || [])
                                  .filter(f => f.active)
                                  .map(f => ({
                                    label: f.name,
                                    icon: f.icon || "⚖",
                                    detail: `${f.stat || ""} — ${f.impact || ""}`.trim().replace(/^— |— $/, ""),
                                  })),
                              };
                            })(),
                          };

                          // Save report to Redis keyed by token — fire and forget
                          try {
                            await fetch("/api/save-report", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ emailKey: token, reportData: reportPayload }),
                            });
                          } catch (e) {
                            console.warn("Could not save report for email failsafe:", e);
                          }

                          // Append token to Stripe payment link as client_reference_id
                          // Stripe passes this back to the webhook automatically
                          window.location.href = `${STRIPE_LINKS.equityUnlock}?client_reference_id=${token}`;
                        }}>
                          Unlock full analysis — $9
                        </button>
                        <div className="lock-also" style={{ marginTop: 10 }}>
                          Or <a onClick={() => setShowPricing(true)}>view all plans</a> — Pro ($19/mo) includes unlimited reports + PDF export
                        </div>
                        <div style={{ marginTop: 12, fontSize: 11, color: "var(--muted)" }}>
                          Cost a barrier?{" "}
                          <a href="mailto:info@tkd-consulting.com" style={{ color: "var(--ink)", textDecoration: "underline", textUnderlineOffset: 3 }}>
                            Email us
                          </a>
                          {" "}— we'll send you a discount code.
                        </div>
                      </div>
                    );
                  })()}

                </div>
              )}

              {/* NEXT STEPS */}
              {result.nextSteps?.length > 0 && (
                <div className="next-section">
                  <div className="col-label" style={{ marginBottom: 4 }}>Your Next Moves</div>
                  {result.nextSteps.map((s, i) => (
                    <div key={i} className="next-item">
                      <span className="next-num">0{i+1}</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* UPGRADE STRIP — free users only, hide once equity unlocked */}
              {!isProReport && !equityPaid && (
                <div className="upgrade-strip">
                  <div className="upgrade-strip-text">
                    <div className="upgrade-strip-label">Want more?</div>
                    <div className="upgrade-strip-headline">Get your full career intelligence report</div>
                    <div className="upgrade-strip-sub">Personal pay gap · Education calibration · Target role roadmap · PDF export</div>
                  </div>
                  <div className="upgrade-strip-actions">
                    <button className="btn-upgrade-primary" onClick={async () => {
                      try { sessionStorage.setItem("cgpx_result", JSON.stringify(result)); } catch {}
                      try { localStorage.setItem("cgpx_result_backup", JSON.stringify(result)); } catch {}

                      // Generate a random token to link this report to the Stripe session
                      const token = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

                      const reportPayload = {
                        name: a.name || "",
                        currentTitle: a.currentTitle || "",
                        industry: a.industry || "",
                        yearsExp: yearsExp,
                        location: `${parsedLoc.city || ""}${parsedLoc.country ? ", " + parsedLoc.country : ""}`,
                        currentLevelTitle: result.currentLevelTitle || bls.band?.title || "",
                        progressionPace: result.progressionPace || "on track",
                        paceDetail: result.paceDetail || "",
                        progressPct: result.progressPct || 50,
                        standout: result.standout || "",
                        summary: result.summary || "",
                        topSkills: result.topSkills || [],
                        nextSteps: result.nextSteps || [],
                        realComparisons: result.realComparisons || [],
                        salP25: salP25,
                        salP50: salP50,
                        salP75: salP75,
                        currencySymbol: sym || "$",
                        currency: geoEntry.currency || "USD",
                        dataSource: geoEntry.source || "BLS OEWS 2023",
                        currentYear: new Date().getFullYear(),
                        equity: (() => {
                          const eq = result.equityAnalysis || {};
                          if (!eq.hasFactors) return null;
                          return {
                            hasFactors: true,
                            salaryGapPct: eq.salaryGapPct || 0,
                            adjustedSalary: eq.adjustedSalaryP50 || salP50,
                            estimatedLevelWithoutBarriers: eq.trueTitle || "",
                            callout: eq.callout || "",
                            factors: (eq.factors || [])
                              .filter(f => f.active)
                              .map(f => ({
                                label: f.name,
                                icon: f.icon || "⚖",
                                detail: `${f.stat || ""} — ${f.impact || ""}`.trim().replace(/^— |— $/, ""),
                              })),
                          };
                        })(),
                      };

                      // Save report to Redis keyed by token — fire and forget
                      try {
                        await fetch("/api/save-report", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ emailKey: token, reportData: reportPayload }),
                        });
                      } catch (e) {
                        console.warn("Could not save report for email failsafe:", e);
                      }

                      // Append token to Stripe payment link as client_reference_id
                      // Stripe passes this back to the webhook automatically
                      window.location.href = `${STRIPE_LINKS.equityUnlock}?client_reference_id=${token}`;
                    }}>
                      Unlock Equity — $9
                    </button>
                    <button className="btn-upgrade-secondary" onClick={() => setShowPricing(true)}>
                      View all plans
                    </button>
                  </div>
                </div>
              )}

              {/* UPGRADE TO PRO STRIP — equity paid users */}
              {!isProReport && equityPaid && (
                <div className="upgrade-strip">
                  <div className="upgrade-strip-text">
                    <div className="upgrade-strip-label">Equity unlocked ✓</div>
                    <div className="upgrade-strip-headline">Upgrade to Pro for unlimited reports + PDF export</div>
                    <div className="upgrade-strip-sub">Personal pay gap · Education calibration · Target role roadmap · PDF export</div>
                  </div>
                  <div className="upgrade-strip-actions">
                    <button className="btn-upgrade-primary" onClick={async () => {
                      try { sessionStorage.setItem("cgpx_result", JSON.stringify(result)); } catch {}
                      try { localStorage.setItem("cgpx_result_backup", JSON.stringify(result)); } catch {}

                      // Generate a random token to link this report to the Stripe session
                      const token = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

                      const reportPayload = {
                        name: a.name || "",
                        currentTitle: a.currentTitle || "",
                        industry: a.industry || "",
                        yearsExp: yearsExp,
                        location: `${parsedLoc.city || ""}${parsedLoc.country ? ", " + parsedLoc.country : ""}`,
                        currentLevelTitle: result.currentLevelTitle || bls.band?.title || "",
                        progressionPace: result.progressionPace || "on track",
                        paceDetail: result.paceDetail || "",
                        progressPct: result.progressPct || 50,
                        standout: result.standout || "",
                        summary: result.summary || "",
                        topSkills: result.topSkills || [],
                        nextSteps: result.nextSteps || [],
                        realComparisons: result.realComparisons || [],
                        salP25: salP25,
                        salP50: salP50,
                        salP75: salP75,
                        currencySymbol: sym || "$",
                        currency: geoEntry.currency || "USD",
                        dataSource: geoEntry.source || "BLS OEWS 2023",
                        currentYear: new Date().getFullYear(),
                        equity: (() => {
                          const eq = result.equityAnalysis || {};
                          if (!eq.hasFactors) return null;
                          return {
                            hasFactors: true,
                            salaryGapPct: eq.salaryGapPct || 0,
                            adjustedSalary: eq.adjustedSalaryP50 || salP50,
                            estimatedLevelWithoutBarriers: eq.trueTitle || "",
                            callout: eq.callout || "",
                            factors: (eq.factors || [])
                              .filter(f => f.active)
                              .map(f => ({
                                label: f.name,
                                icon: f.icon || "⚖",
                                detail: `${f.stat || ""} — ${f.impact || ""}`.trim().replace(/^— |— $/, ""),
                              })),
                          };
                        })(),
                      };

                      // Save report to Redis keyed by token — fire and forget
                      try {
                        await fetch("/api/save-report", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ emailKey: token, reportData: reportPayload }),
                        });
                      } catch (e) {
                        console.warn("Could not save report for email failsafe:", e);
                      }

                      // Append token to Stripe payment link as client_reference_id
                      // Stripe passes this back to the webhook automatically
                      window.location.href = `${STRIPE_LINKS.pro}?client_reference_id=${token}`;
                    }}>
                      Upgrade to Pro — $19/mo
                    </button>
                    <button className="btn-upgrade-secondary" onClick={() => setShowPricing(true)}>
                      View all plans
                    </button>
                  </div>
                </div>
              )}

              {/* FOOTER */}
              <div className="card-footer">
                <div className="footer-brand">Career GPX {isProReport && <span className="pro-badge" style={{fontSize:9,marginLeft:8}}>✦ Pro</span>}</div>
                <div className="footer-source">
                  Salary data: {sourceLabel} {sourceYear} · Equity research: McKinsey, Williams Institute<br />
                  {a.industry} · {parsedLoc.city}, {parsedLoc.country} · {yearsExp} years experience
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="result-actions">
              {(isProReport || equityPaid) && (
                <button className="btn-pdf" onClick={() => exportToPDF(result, bls, parsedLoc, salP25, salP50, salP75, sym, tierMeta).catch(console.error)}>
                  ↓ Export PDF Report
                </button>
              )}
              <button className="btn-share" onClick={() => setShowShare(true)}>
                Share My Report ↗
              </button>
              <button className="btn-restart" onClick={restart}>Start Over</button>
            </div>

            {/* SHARE MODAL */}
            {showShare && (() => {
              const shareText = `I just got my career intelligence report from Career GPX — ${result.currentLevelTitle} level, ${yearsExp} years in ${a.industry}. Check yours 👇`;
              const shareUrl = "https://careergpx.com";
              const emailBody = `${shareText}\n\n${shareUrl}`;
              const encoded = encodeURIComponent;

              const downloadCard = () => {
                const canvas = document.createElement("canvas");
                const W = 1200, H = 630;
                canvas.width = W; canvas.height = H;
                const ctx = canvas.getContext("2d");

                // Background
                ctx.fillStyle = "#0a0a0f";
                ctx.fillRect(0, 0, W, H);

                // Gold accent bar top
                ctx.fillStyle = "#c9a84c";
                ctx.fillRect(0, 0, W, 6);

                // Red accent bar bottom
                ctx.fillStyle = "#e63946";
                ctx.fillRect(0, H - 6, W, 6);

                // Subtle grid pattern
                ctx.strokeStyle = "rgba(255,255,255,0.03)";
                ctx.lineWidth = 1;
                for (let x = 0; x < W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
                for (let y = 0; y < H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

                // Brand name
                ctx.fillStyle = "#fafaf7";
                ctx.font = "bold 28px Georgia, serif";
                ctx.fillText("Career GPX", 60, 70);

                // Divider
                ctx.strokeStyle = "#e63946";
                ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(60, 90); ctx.lineTo(W - 60, 90); ctx.stroke();

                // Level badge
                ctx.fillStyle = "#e63946";
                ctx.beginPath();
                ctx.roundRect(60, 120, 200, 44, 4);
                ctx.fill();
                ctx.fillStyle = "#fafaf7";
                ctx.font = "bold 20px Georgia, serif";
                ctx.fillText(result.currentLevelTitle || bls.band.title, 80, 149);

                // Main headline
                ctx.fillStyle = "#fafaf7";
                ctx.font = "bold 72px Georgia, serif";
                ctx.fillText(`${sym}${Math.round(salP50/1000)}K`, 60, 260);

                ctx.fillStyle = "#888880";
                ctx.font = "24px Arial, sans-serif";
                ctx.fillText("median salary for your level & location", 60, 300);

                // Stats row
                const stats = [
                  { label: "Experience", value: `${yearsExp} years` },
                  { label: "Industry", value: a.industry?.split("/")[0]?.trim() || a.industry },
                  { label: "Location", value: parsedLoc.city || parsedLoc.country },
                ];
                stats.forEach((s, i) => {
                  const x = 60 + i * 360;
                  ctx.fillStyle = "#c9a84c";
                  ctx.font = "bold 13px Arial, sans-serif";
                  ctx.fillText(s.label.toUpperCase(), x, 380);
                  ctx.fillStyle = "#fafaf7";
                  ctx.font = "bold 26px Arial, sans-serif";
                  ctx.fillText(s.value, x, 415);
                });

                // Divider
                ctx.strokeStyle = "rgba(255,255,255,0.1)";
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(60, 450); ctx.lineTo(W - 60, 450); ctx.stroke();

                // CTA
                ctx.fillStyle = "#888880";
                ctx.font = "18px Arial, sans-serif";
                ctx.fillText("Get your own career intelligence report at", 60, 490);
                ctx.fillStyle = "#c9a84c";
                ctx.font = "bold 18px Arial, sans-serif";
                ctx.fillText("careergpx.com", 60, 520);

                // Watermark
                ctx.fillStyle = "rgba(255,255,255,0.08)";
                ctx.font = "bold 120px Georgia, serif";
                ctx.fillText("GPX", W - 320, H - 60);

                const link = document.createElement("a");
                link.download = "careergpx-report-card.png";
                link.href = canvas.toDataURL("image/png");
                link.click();
              };

              return (
                <div className="share-overlay" onClick={() => setShowShare(false)}>
                  <div className="share-modal" onClick={e => e.stopPropagation()}>
                    <div className="share-modal-title">Share your report</div>
                    <div className="share-modal-sub">
                      Download your result card to share on social — or copy the text below and post it with the link. The link goes to careergpx.com so your network can get their own report.
                    </div>

                    {/* Download card button */}
                    <button onClick={downloadCard} style={{ width: "100%", padding: "14px", background: "var(--ink)", color: "var(--paper)", border: "none", borderRadius: "var(--r)", fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      ↓ Download Result Card (PNG)
                    </button>

                    {/* PDF download — unlocked users only */}
                    {(isProReport || equityPaid) && (
                      <button onClick={() => { setShowShare(false); exportToPDF(result, bls, parsedLoc, salP25, salP50, salP75, sym, tierMeta).catch(console.error); }} style={{ width: "100%", padding: "14px", background: "transparent", color: "var(--ink)", border: "1.5px solid var(--ink)", borderRadius: "var(--r)", fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        ↓ Download Full Report (PDF)
                      </button>
                    )}

                    {/* Copyable text */}
                    <div style={{ background: "var(--paper2)", border: "1.5px solid var(--rule)", borderRadius: "var(--r)", padding: "12px 14px", fontSize: 13, color: "var(--ink)", lineHeight: 1.6, marginBottom: 16 }}>
                      {shareText}
                      <button onClick={() => {
                        navigator.clipboard?.writeText(shareText);
                        const btn = document.querySelector(".copy-text-btn");
                        if (btn) { btn.textContent = "Copied!"; setTimeout(() => { btn.textContent = "Copy text"; }, 2000); }
                      }} className="copy-text-btn" style={{ display: "block", marginTop: 8, fontSize: 12, color: "var(--muted)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-body)", textDecoration: "underline" }}>
                        Copy text
                      </button>
                    </div>

                    <div className="share-buttons">
                      <a className="share-btn"
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded(shareUrl)}`}
                        target="_blank" rel="noopener noreferrer">
                        <span className="share-btn-icon">💼</span> Share on LinkedIn
                      </a>
                      <a className="share-btn"
                        href={`https://twitter.com/intent/tweet?text=${encoded(shareText)}&url=${encoded(shareUrl)}`}
                        target="_blank" rel="noopener noreferrer">
                        <span className="share-btn-icon">𝕏</span> Share on X
                      </a>
                      <a className="share-btn"
                        href={`https://wa.me/?text=${encoded(shareText + " " + shareUrl)}`}
                        target="_blank" rel="noopener noreferrer">
                        <span className="share-btn-icon">💬</span> Share on WhatsApp
                      </a>
                      <a className="share-btn"
                        href={`https://reddit.com/submit?url=${encoded(shareUrl)}&title=${encoded("I just benchmarked my career with Career GPX — " + result.currentLevelTitle + " level, " + yearsExp + " years in " + a.industry)}`}
                        target="_blank" rel="noopener noreferrer">
                        <span className="share-btn-icon">🤖</span> Share on Reddit
                      </a>
                      <a className="share-btn"
                        href={`mailto:?subject=${encoded("My Career GPX Report")}&body=${encoded(emailBody)}`}>
                        <span className="share-btn-icon">✉️</span> Send via Email
                      </a>
                    </div>
                    <button className="share-close" onClick={() => setShowShare(false)}>Close</button>
                  </div>
                </div>
              );
            })()}

            <div className="disclaimer">
              <strong>About this report:</strong> Salary figures show what people at your experience level and location typically earn, sourced from {sourceLabel} {sourceYear}. We do not know your actual salary.<br />
              Equity gap estimates apply published population-level research (McKinsey, Williams Institute) to your demographic profile — they reflect average gaps across millions of workers, not a personal pay audit.<br />
              Individual outcomes vary significantly by company, role, negotiation, and circumstance. This report is directional, not a guarantee of market value.
            </div>
            <div style={{ textAlign: "center", padding: "16px 0 32px", fontSize: 12, color: "var(--muted)" }}>
              <a href="/terms" style={{ color: "var(--muted)", textDecoration: "underline", textUnderlineOffset: 3 }}>Terms of Service</a>
              <span style={{ margin: "0 8px" }}>·</span>
              <a href="/privacy" style={{ color: "var(--muted)", textDecoration: "underline", textUnderlineOffset: 3 }}>Privacy Policy</a>
              <span style={{ margin: "0 8px" }}>·</span>
              © 2026 TKD Consulting LLC
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
}
